import { router } from '@inertiajs/react'
import { useState, useEffect } from 'react'
import {
  ArrowLeftIcon,
  CalendarIcon,
  CheckCircle2Icon,
  ChevronsUpDown,
  ClipboardListIcon,
  FileTextIcon,
  FlaskConicalIcon,
  HeartPulseIcon,
  PhoneIcon,
  PillIcon,
  PlusIcon,
  Trash2Icon,
  UploadIcon,
  UserIcon,
  AlertCircleIcon,
  MapPinIcon,
  CreditCardIcon,
  ActivityIcon,
  ClipboardIcon,
  XIcon,
} from 'lucide-react'

import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { AppHeader } from '@/components/app-header'
import { Progress } from '@/components/ui/progress'
import { Separator } from '@/components/ui/separator'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { Textarea } from '@/components/ui/textarea'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover'
import { toast } from 'sonner'
import { SmartPhraseTextarea } from '@/components/smart-phrase-textarea'
import { QuestionnaireResponsesModal } from '@/components/questionnaire-responses-modal'
import ProviderIntakeDialog from '@/components/provider-intake-dialog'
import { RiskAssessmentIntakeDialog, type QuestionnaireQuestion as QQuestion } from '@/components/risk-assessment-intake-dialog'

interface Patient {
  id: number
  display_name: string
  legal_name: string
  alias_name: string | null
  date_of_birth: string | null
  age: number | null
  phone_number: string
  email: string | null
  zip_code: string | null
  home_address: string | null
  allergies: string | null
  sex_assigned_at_birth: string | null
  site_name: string
  site_id: number
  emergency_contacts: Array<{ name: string; phone: string }>
  prescription: {
    plan_name: string | null
    id_number: string | null
    bin: string | null
    group_number: string | null
    pcn: string | null
  }
  consents_complete: boolean
  consent_signed_at: string | null
  created_at: string
}

interface StatusHistoryEntry {
  status: string
  status_label: string
  changed_at: string
}

interface CareCase {
  id: number
  case_status: string
  status_label: string
  priority: string
  priority_label: string
  priority_color: string
  treatment_status: string | null
  treatment_status_label: string | null
  opened_at: string
  closed_at: string | null
  closed_reason: string | null
  last_stage_update_at: string
  days_open: number
  days_since_last_activity: number
  active: boolean
  cured: boolean
  status_history: StatusHistoryEntry[]
}

interface QuestionnaireResponse {
  question_id: number
  section_name: string
  question_text: string
  selected_response_text: string
  point_value: number
  display_order: number
}

interface RiskAssessment {
  id: number
  calculated_risk_score: string
  risk_level_label: string
  risk_color: string
  completed_at: string
  counselor_summary: string | null
  responses: Record<string, any>
  questionnaire_responses?: QuestionnaireResponse[]
  questionnaire_by_section?: Record<string, QuestionnaireResponse[]>
}

interface LabOrder {
  id: number
  order_type: string
  order_type_label: string
  status: string
  status_label: string
  ordered_at: string
  collected_at: string | null
  received_at: string | null
  result_value: any
  result_notes: string | null
  days_since_ordered: number
  screening_lab: boolean
  baseline_lab: boolean
  svr12_lab: boolean
  has_files?: boolean
}

interface TreatmentPlan {
  id: number
  instructions: string
  created_by_name: string
  last_updated_by_name: string
  created_at: string
  updated_at: string
}

interface ProviderIntake {
  id: number
  intake_started_at: string
  intake_completed_at: string | null
  cmp_done: boolean
  cbc_done: boolean
  hep_b_done: boolean
  fibrosis_done: boolean
  hiv_done: boolean
  genotype_done: boolean
  all_checks_complete: boolean
  completion_percentage: number
  pending_checks: string[]
  completed_checks: string[]
  clinical_notes: string | null
  days_in_progress: number
}

interface Treatment {
  id: number
  regimen_name: string
  duration_weeks: number
  treatment_duration_label?: string
  treatment_directions?: string
  prior_auth_status: string | null
  prior_auth_status_label: string
  treatment_start_date: string
  treatment_end_date: string
  adherence_summary: string | null
  side_effects_summary: string | null
  last_refill_logged_at: string | null
  active: boolean
  completed: boolean
  days_remaining: number
  days_elapsed: number
  weeks_remaining: number
  weeks_elapsed: number
  completion_percentage: number
  status_label: string
}

interface FollowUp {
  id: number
  follow_up_type: string
  type_label: string
  due_on: string
  completed_at: string | null
  adherence_notes: string | null
  side_effect_notes: string | null
  provider_notes: string | null
  completed: boolean
  pending: boolean
  overdue: boolean
  days_overdue: number
  days_until_due: number
  status_label: string
  status_color: string
}

interface CaseNote {
  id: number
  note_type: string | null
  note_type_label: string
  body: string
  author_name: string
  created_at: string
}

interface ActiveMedication {
  id: number
  medication_name: string
  notes: string | null
  created_at: string
}

interface ProblemListItem {
  id: number
  icd10_code: string
  diagnosis_name: string
  status: string
  onset_date: string | null
  resolved_date: string | null
  notes: string | null
  creator_name: string
  updater_name: string
  created_at: string
  updated_at: string
}

interface Appointment {
  id: number
  appointment_type: string
  type_label: string
  scheduled_at: string
  formatted_date: string
  formatted_time: string
  formatted_datetime: string
  duration_minutes: number
  location: string | null
  reason: string | null
  status: string
  status_label: string
  site_name: string
  upcoming: boolean
  past: boolean
}

interface AppointmentsSummary {
  upcoming: Appointment[]
  past: Appointment[]
}

interface AdminPatientsShowProps {
  auth: {
    user: {
      id: number
      name: string
      email: string
      admin: boolean
    }
  }
  patient: Patient
  care_case: CareCase | null
  risk_assessment: RiskAssessment | null
  questionnaire_questions: QQuestion[]
  lab_orders: LabOrder[]
  provider_intake: ProviderIntake | null
  treatment: Treatment | null
  treatment_plan: TreatmentPlan | null
  follow_ups: FollowUp[]
  case_notes: CaseNote[]
  active_medications: ActiveMedication[]
  problem_list_items: ProblemListItem[]
  appointments: AppointmentsSummary
  care_stage_options: string[]
  treatment_options: {
    regimen_options: string[]
    duration_options: string[]
    directions_options: string[]
    prior_auth_statuses: string[]
  }
}

export default function AdminPatientsShow({
  auth,
  patient,
  care_case,
  risk_assessment,
  questionnaire_questions,
  lab_orders,
  provider_intake,
  treatment,
  treatment_plan,
  follow_ups,
  case_notes,
  active_medications,
  problem_list_items,
  appointments,
  care_stage_options,
  treatment_options,
}: AdminPatientsShowProps) {
  // State for dialogs and forms
  const [questionnaireModalOpen, setQuestionnaireModalOpen] = useState(false)
  const [assessmentIntakeOpen, setAssessmentIntakeOpen] = useState(false)
  const [priorityDialogOpen, setPriorityDialogOpen] = useState(false)
  const [selectedPriority, setSelectedPriority] = useState(care_case?.priority || 'standard')
  const [isUpdatingPriority, setIsUpdatingPriority] = useState(false)

  const [showDeleteDialog, setShowDeleteDialog] = useState(false)
  const [deleteConfirmText, setDeleteConfirmText] = useState('')

  const [caseStatusDialogOpen, setCaseStatusDialogOpen] = useState(false)
  const [selectedCaseStatus, setSelectedCaseStatus] = useState(care_case?.case_status || '')
  const [isUpdatingCaseStatus, setIsUpdatingCaseStatus] = useState(false)

  const [treatmentStatusDialogOpen, setTreatmentStatusDialogOpen] = useState(false)
  const [selectedTreatmentStatus, setSelectedTreatmentStatus] = useState(care_case?.treatment_status || '')
  const [isUpdatingTreatmentStatus, setIsUpdatingTreatmentStatus] = useState(false)

  const [careStageDialogOpen, setCareStageDialogOpen] = useState(false)
  const [selectedCareStage, setSelectedCareStage] = useState(care_case?.case_status || '')
  const [isUpdatingCareStage, setIsUpdatingCareStage] = useState(false)

  const [emergencyContactsDialogOpen, setEmergencyContactsDialogOpen] = useState(false)
  const [emergencyContact1Name, setEmergencyContact1Name] = useState(patient.emergency_contacts[0]?.name || '')
  const [emergencyContact1Phone, setEmergencyContact1Phone] = useState(patient.emergency_contacts[0]?.phone || '')
  const [emergencyContact2Name, setEmergencyContact2Name] = useState(patient.emergency_contacts[1]?.name || '')
  const [emergencyContact2Phone, setEmergencyContact2Phone] = useState(patient.emergency_contacts[1]?.phone || '')
  const [emergencyContact3Name, setEmergencyContact3Name] = useState(patient.emergency_contacts[2]?.name || '')
  const [emergencyContact3Phone, setEmergencyContact3Phone] = useState(patient.emergency_contacts[2]?.phone || '')
  const [isUpdatingEmergencyContacts, setIsUpdatingEmergencyContacts] = useState(false)

  const [prescriptionDialogOpen, setPrescriptionDialogOpen] = useState(false)
  const [prescriptionPlanName, setPrescriptionPlanName] = useState(patient.prescription.plan_name || '')
  const [prescriptionIdNumber, setPrescriptionIdNumber] = useState(patient.prescription.id_number || '')
  const [prescriptionBin, setPrescriptionBin] = useState(patient.prescription.bin || '')
  const [prescriptionGroupNumber, setPrescriptionGroupNumber] = useState(patient.prescription.group_number || '')
  const [prescriptionPcn, setPrescriptionPcn] = useState(patient.prescription.pcn || '')
  const [isUpdatingPrescription, setIsUpdatingPrescription] = useState(false)

  const [demographicsDialogOpen, setDemographicsDialogOpen] = useState(false)
  const [legalName, setLegalName] = useState(patient.legal_name || '')
  const [aliasName, setAliasName] = useState(patient.alias_name || '')
  const [phoneNumber, setPhoneNumber] = useState(patient.phone_number || '')
  const [email, setEmail] = useState(patient.email || '')
  const [zipCode, setZipCode] = useState(patient.zip_code || '')
  const [homeAddress, setHomeAddress] = useState(patient.home_address || '')
  const [allergies, setAllergies] = useState(patient.allergies || '')
  const [sexAssignedAtBirth, setSexAssignedAtBirth] = useState(patient.sex_assigned_at_birth || '')
  const [isUpdatingDemographics, setIsUpdatingDemographics] = useState(false)

  const [medicationDialogOpen, setMedicationDialogOpen] = useState(false)
  const [newMedicationName, setNewMedicationName] = useState('')
  const [newMedicationNotes, setNewMedicationNotes] = useState('')
  const [isAddingMedication, setIsAddingMedication] = useState(false)

  const [treatmentDialogOpen, setTreatmentDialogOpen] = useState(false)
  const [treatmentRegimenName, setTreatmentRegimenName] = useState(treatment?.regimen_name || '')
  const [treatmentDurationLabel, setTreatmentDurationLabel] = useState(treatment?.treatment_duration_label || '')
  const [treatmentDirections, setTreatmentDirections] = useState(treatment?.treatment_directions || '')
  const [treatmentStartDate, setTreatmentStartDate] = useState(treatment?.treatment_start_date || '')
  const [treatmentPriorAuthStatus, setTreatmentPriorAuthStatus] = useState(treatment?.prior_auth_status || '')
  const [treatmentCompletionDate, setTreatmentCompletionDate] = useState('')
  const [isUpdatingTreatment, setIsUpdatingTreatment] = useState(false)

  // Calculate treatment completion date when start date or duration changes
  useEffect(() => {
    if (treatmentStartDate && treatmentDurationLabel) {
      const durationWeeks = parseInt(treatmentDurationLabel)
      if (!isNaN(durationWeeks)) {
        const startDate = new Date(treatmentStartDate)
        const endDate = new Date(startDate)
        endDate.setDate(endDate.getDate() + (durationWeeks * 7))
        setTreatmentCompletionDate(endDate.toISOString().split('T')[0])
      }
    } else {
      setTreatmentCompletionDate('')
    }
  }, [treatmentStartDate, treatmentDurationLabel])

  const [problemListDialogOpen, setProblemListDialogOpen] = useState(false)
  const [useManualEntry, setUseManualEntry] = useState(false)
  const [selectedIcd10CodeId, setSelectedIcd10CodeId] = useState<number | null>(null)
  const [icd10CodeOptions, setIcd10CodeOptions] = useState<any[]>([])
  const [icd10SearchQuery, setIcd10SearchQuery] = useState('')
  const [isLoadingCodes, setIsLoadingCodes] = useState(false)
  const [newDiagnosisIcd10Code, setNewDiagnosisIcd10Code] = useState('')
  const [newDiagnosisName, setNewDiagnosisName] = useState('')
  const [newDiagnosisOnsetDate, setNewDiagnosisOnsetDate] = useState('')
  const [newDiagnosisNotes, setNewDiagnosisNotes] = useState('')
  const [isAddingDiagnosis, setIsAddingDiagnosis] = useState(false)

  const [treatmentPlanInstructions, setTreatmentPlanInstructions] = useState(treatment_plan?.instructions || '')

  const [caseNoteDialogOpen, setCaseNoteDialogOpen] = useState(false)
  const [caseNoteBody, setCaseNoteBody] = useState('')
  const [caseNoteType, setCaseNoteType] = useState('general')
  const [isSavingCaseNote, setIsSavingCaseNote] = useState(false)

  const [labUploadDialogOpen, setLabUploadDialogOpen] = useState(false)
  const [selectedLabOrder, setSelectedLabOrder] = useState<LabOrder | null>(null)
  const [labResultFile, setLabResultFile] = useState<File | null>(null)
  const [labResultValue, setLabResultValue] = useState('')
  const [labResultNotes, setLabResultNotes] = useState('')
  const [isUploadingLabResult, setIsUploadingLabResult] = useState(false)

  const [createLabOrderDialogOpen, setCreateLabOrderDialogOpen] = useState(false)
  const [newLabOrderType, setNewLabOrderType] = useState('')
  const [newLabOrderDate, setNewLabOrderDate] = useState(new Date().toISOString().split('T')[0])
  const [newLabOrderStatus, setNewLabOrderStatus] = useState('ordered')
  const [newLabOrderResultValue, setNewLabOrderResultValue] = useState('')
  const [newLabOrderResultNotes, setNewLabOrderResultNotes] = useState('')
  const [newLabOrderFiles, setNewLabOrderFiles] = useState<FileList | null>(null)
  const [isCreatingLabOrder, setIsCreatingLabOrder] = useState(false)

  const [followUpDialogOpen, setFollowUpDialogOpen] = useState(false)
  const [selectedFollowUp, setSelectedFollowUp] = useState<FollowUp | null>(null)
  const [followUpAction, setFollowUpAction] = useState<'reschedule' | 'complete'>('reschedule')
  const [followUpDueOn, setFollowUpDueOn] = useState('')
  const [followUpAdherenceNotes, setFollowUpAdherenceNotes] = useState('')
  const [followUpSideEffectNotes, setFollowUpSideEffectNotes] = useState('')
  const [followUpProviderNotes, setFollowUpProviderNotes] = useState('')
  const [isUpdatingFollowUp, setIsUpdatingFollowUp] = useState(false)

  // File preview state
  const [filePreviewOpen, setFilePreviewOpen] = useState(false)
  const [previewFile, setPreviewFile] = useState<{ url: string; filename: string; content_type: string } | null>(null)

  const getCaseStatusBadgeVariant = (status: string): "default" | "secondary" | "destructive" | "outline" => {
    switch (status) {
      case 'cured':
        return 'default'
      case 'on_tx':
        return 'secondary'
      case 'closed_negative':
        return 'outline'
      default:
        return 'secondary'
    }
  }

  const getRiskBadgeVariant = (level: string): "default" | "secondary" | "destructive" => {
    switch (level.toLowerCase()) {
      case 'high':
        return 'destructive'
      case 'moderate':
        return 'secondary'
      default:
        return 'default'
    }
  }

  const getRiskBadgeColorClasses = (level: string): string => {
    switch (level.toLowerCase()) {
      case 'low':
        return 'bg-green-100 dark:bg-green-900/30 text-green-800 dark:text-green-300 hover:bg-green-200 dark:hover:bg-green-900/50'
      case 'moderate':
        return 'bg-yellow-100 dark:bg-yellow-900/30 text-yellow-800 dark:text-yellow-300 hover:bg-yellow-200 dark:hover:bg-yellow-900/50'
      case 'high':
        return 'bg-red-100 dark:bg-red-900/30 text-red-800 dark:text-red-300 hover:bg-red-200 dark:hover:bg-red-900/50'
      case 'critical':
        return 'bg-red-200 dark:bg-red-900/50 text-red-900 dark:text-red-200 hover:bg-red-300 dark:hover:bg-red-900/70'
      default:
        return 'bg-green-100 dark:bg-green-900/30 text-green-800 dark:text-green-300 hover:bg-green-200 dark:hover:bg-green-900/50'
    }
  }

  const getPriorityBadgeVariant = (priority: string): "default" | "secondary" | "destructive" => {
    switch (priority) {
      case 'urgent':
        return 'destructive'
      case 'high':
        return 'secondary'
      default:
        return 'default'
    }
  }

  // Handler: Update Priority
  const handleUpdatePriority = async () => {
    if (!care_case) return

    setIsUpdatingPriority(true)
    try {
      const response = await fetch(`/admin/patients/${patient.id}/update_priority`, {
        method: 'PATCH',
        headers: getAuthHeaders(),
        body: JSON.stringify({ priority: selectedPriority }),
      })

      const data = await response.json()

      if (data.success) {
        toast.success('Priority updated successfully')
        router.reload()
        setPriorityDialogOpen(false)
      } else {
        toast.error(data.errors?.join(', ') || 'Failed to update priority')
      }
    } catch (error) {
      toast.error('An error occurred while updating priority')
    } finally {
      setIsUpdatingPriority(false)
    }
  }

  // Handler: Update Case Status
  const handleUpdateCaseStatus = async () => {
    if (!care_case) return

    setIsUpdatingCaseStatus(true)
    try {
      const response = await fetch(`/admin/patients/${patient.id}/update_care_stage`, {
        method: 'PATCH',
        headers: getAuthHeaders(),
        body: JSON.stringify({ stage: selectedCaseStatus }),
      })

      const data = await response.json()

      if (data.success) {
        toast.success('Case status updated successfully')
        router.reload()
        setCaseStatusDialogOpen(false)
      } else {
        toast.error(data.errors?.join(', ') || 'Failed to update case status')
      }
    } catch (error) {
      toast.error('An error occurred while updating case status')
    } finally {
      setIsUpdatingCaseStatus(false)
    }
  }

  // Handler: Update Treatment Status
  const handleUpdateTreatmentStatus = async () => {
    if (!care_case) return

    setIsUpdatingTreatmentStatus(true)
    try {
      const response = await fetch(`/admin/patients/${patient.id}/update_treatment_status`, {
        method: 'PATCH',
        headers: getAuthHeaders(),
        body: JSON.stringify({ treatment_status: selectedTreatmentStatus }),
      })

      const data = await response.json()

      if (data.success) {
        toast.success('Treatment status updated successfully')
        router.reload()
        setTreatmentStatusDialogOpen(false)
      } else {
        toast.error(data.errors?.join(', ') || 'Failed to update treatment status')
      }
    } catch (error) {
      toast.error('An error occurred while updating treatment status')
    } finally {
      setIsUpdatingTreatmentStatus(false)
    }
  }

  // Handler: Create Case Note
  const handleCreateCaseNote = async () => {
    setIsSavingCaseNote(true)
    try {
      const response = await fetch(`/admin/patients/${patient.id}/create_case_note`, {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify({ body: caseNoteBody, note_type: caseNoteType }),
      })

      const data = await response.json()

      if (data.success) {
        toast.success('Case note created successfully')
        router.reload()
        setCaseNoteDialogOpen(false)
        setCaseNoteBody('')
        setCaseNoteType('general')
      } else {
        toast.error(data.errors?.join(', ') || 'Failed to create case note')
      }
    } catch (error) {
      toast.error('An error occurred while creating case note')
    } finally {
      setIsSavingCaseNote(false)
    }
  }

  // Handler: Upload Lab Result
  const handleUploadLabResult = async () => {
    if (!selectedLabOrder) return

    setIsUploadingLabResult(true)
    try {
      const formData = new FormData()
      if (labResultFile) formData.append('result_file', labResultFile)
      if (labResultValue) formData.append('result_value', labResultValue)
      if (labResultNotes) formData.append('result_notes', labResultNotes)
      formData.append('status', 'result_received')

      const response = await fetch(`/admin/patients/${patient.id}/lab_orders/${selectedLabOrder.id}/upload_result`, {
        method: 'POST',
        headers: getFormDataAuthHeaders(),
        body: formData,
      })

      const data = await response.json()

      if (response.ok && data.success) {
        toast.success('Lab result uploaded successfully')
        router.reload()
        setLabUploadDialogOpen(false)
        setSelectedLabOrder(null)
        setLabResultFile(null)
        setLabResultValue('')
        setLabResultNotes('')
      } else {
        // Handle specific error cases
        if (response.status === 403) {
          toast.error('You are not authorized to upload lab results for this patient')
        } else if (response.status === 404) {
          toast.error('Lab order not found')
        } else {
          toast.error(data.errors?.join(', ') || data.error || 'Failed to upload lab result')
        }
      }
    } catch (error) {
      toast.error('An error occurred while uploading lab result')
    } finally {
      setIsUploadingLabResult(false)
    }
  }

  // Handler: Create Lab Order
  const handleCreateLabOrder = async () => {
    if (!newLabOrderType || !newLabOrderDate) {
      toast.error('Please select a test type and order date')
      return
    }

    setIsCreatingLabOrder(true)
    try {
      const formData = new FormData()
      formData.append('order_type', newLabOrderType)
      formData.append('ordered_at', new Date(newLabOrderDate).toISOString())
      formData.append('status', newLabOrderStatus)
      if (newLabOrderResultValue) formData.append('result_value', newLabOrderResultValue)
      if (newLabOrderResultNotes) formData.append('result_notes', newLabOrderResultNotes)
      if (newLabOrderFiles) {
        Array.from(newLabOrderFiles).forEach((file) => {
          formData.append('result_files[]', file)
        })
      }

      const response = await fetch(`/admin/patients/${patient.id}/lab_orders`, {
        method: 'POST',
        headers: getFormDataAuthHeaders(),
        body: formData,
      })

      const data = await response.json()

      if (response.ok && data.success) {
        toast.success('Lab order created successfully')
        router.reload()
        setCreateLabOrderDialogOpen(false)
        setNewLabOrderType('')
        setNewLabOrderDate(new Date().toISOString().split('T')[0])
        setNewLabOrderStatus('ordered')
        setNewLabOrderResultValue('')
        setNewLabOrderResultNotes('')
        setNewLabOrderFiles(null)
      } else {
        // Handle specific error cases
        if (response.status === 403) {
          toast.error('You are not authorized to create lab orders for this patient')
        } else if (response.status === 404) {
          toast.error('Patient must have an active care case to create lab orders')
        } else {
          toast.error(data.errors?.join(', ') || data.error || 'Failed to create lab order')
        }
      }
    } catch (error) {
      toast.error('An error occurred while creating lab order')
    } finally {
      setIsCreatingLabOrder(false)
    }
  }

  // Handler: Update Follow-Up
  const handleUpdateFollowUp = async () => {
    if (!selectedFollowUp) return

    setIsUpdatingFollowUp(true)
    try {
      const payload: Record<string, any> = {}

      if (followUpAction === 'reschedule' && followUpDueOn) {
        payload.due_on = followUpDueOn
      } else if (followUpAction === 'complete') {
        payload.mark_completed = 'true'
        if (followUpAdherenceNotes) payload.adherence_notes = followUpAdherenceNotes
        if (followUpSideEffectNotes) payload.side_effect_notes = followUpSideEffectNotes
        if (followUpProviderNotes) payload.provider_notes = followUpProviderNotes
      }

      const response = await fetch(`/admin/patients/${patient.id}/follow_ups/${selectedFollowUp.id}`, {
        method: 'PATCH',
        headers: getAuthHeaders(),
        body: JSON.stringify(payload),
      })

      const data = await response.json()

      if (data.success) {
        toast.success('Follow-up updated successfully')
        router.reload()
        setFollowUpDialogOpen(false)
        setSelectedFollowUp(null)
        setFollowUpDueOn('')
        setFollowUpAdherenceNotes('')
        setFollowUpSideEffectNotes('')
        setFollowUpProviderNotes('')
      } else {
        toast.error(data.errors?.join(', ') || 'Failed to update follow-up')
      }
    } catch (error) {
      toast.error('An error occurred while updating follow-up')
    } finally {
      setIsUpdatingFollowUp(false)
    }
  }

  // Handler: Update Care Stage
  const handleUpdateCareStage = async () => {
    setIsUpdatingCareStage(true)
    try {
      const response = await fetch(`/admin/patients/${patient.id}/update_care_stage`, {
        method: 'PATCH',
        headers: getAuthHeaders(),
        body: JSON.stringify({ stage: selectedCareStage }),
      })

      const data = await response.json()

      if (data.success) {
        toast.success('Care stage updated successfully')
        router.reload()
        setCareStageDialogOpen(false)
      } else {
        toast.error(data.errors?.join(', ') || 'Failed to update care stage')
      }
    } catch (error) {
      toast.error('An error occurred while updating care stage')
    } finally {
      setIsUpdatingCareStage(false)
    }
  }

  // Handler: Update Emergency Contacts
  const handleUpdateEmergencyContacts = async () => {
    setIsUpdatingEmergencyContacts(true)
    try {
      const response = await fetch(`/admin/patients/${patient.id}/update_emergency_contacts`, {
        method: 'PATCH',
        headers: getAuthHeaders(),
        body: JSON.stringify({
          contacts: {
            contact_1_name: emergencyContact1Name,
            contact_1_phone: emergencyContact1Phone,
            contact_2_name: emergencyContact2Name,
            contact_2_phone: emergencyContact2Phone,
            contact_3_name: emergencyContact3Name,
            contact_3_phone: emergencyContact3Phone,
          },
        }),
      })

      const data = await response.json()

      if (data.success) {
        toast.success('Emergency contacts updated successfully')
        router.reload()
        setEmergencyContactsDialogOpen(false)
      } else {
        toast.error(data.errors?.join(', ') || 'Failed to update emergency contacts')
      }
    } catch (error) {
      toast.error('An error occurred while updating emergency contacts')
    } finally {
      setIsUpdatingEmergencyContacts(false)
    }
  }

  // Handler: Update Prescription Info
  const handleUpdatePrescription = async () => {
    setIsUpdatingPrescription(true)
    try {
      const response = await fetch(`/admin/patients/${patient.id}/update_prescription_info`, {
        method: 'PATCH',
        headers: getAuthHeaders(),
        body: JSON.stringify({
          prescription: {
            plan_name: prescriptionPlanName,
            id_number: prescriptionIdNumber,
            bin: prescriptionBin,
            group_number: prescriptionGroupNumber,
            pcn: prescriptionPcn,
          },
        }),
      })

      const data = await response.json()

      if (data.success) {
        toast.success('Prescription information updated successfully')
        router.reload()
        setPrescriptionDialogOpen(false)
      } else {
        toast.error(data.errors?.join(', ') || 'Failed to update prescription information')
      }
    } catch (error) {
      toast.error('An error occurred while updating prescription information')
    } finally {
      setIsUpdatingPrescription(false)
    }
  }

  // Handler: Update Demographics
  const handleUpdateDemographics = async () => {
    setIsUpdatingDemographics(true)
    try {
      const response = await fetch(`/admin/patients/${patient.id}/update_demographics`, {
        method: 'PATCH',
        headers: getAuthHeaders(),
        body: JSON.stringify({
          demographics: {
            legal_name: legalName,
            alias_name: aliasName,
            phone_number: phoneNumber,
            email: email,
            zip_code: zipCode,
            home_address: homeAddress,
            allergies: allergies,
            sex_assigned_at_birth: sexAssignedAtBirth,
            emergency_contact_1_name: emergencyContact1Name,
            emergency_contact_1_phone: emergencyContact1Phone,
            emergency_contact_2_name: emergencyContact2Name,
            emergency_contact_2_phone: emergencyContact2Phone,
            emergency_contact_3_name: emergencyContact3Name,
            emergency_contact_3_phone: emergencyContact3Phone,
          },
        }),
      })

      const data = await response.json()

      if (data.success) {
        toast.success('Patient information updated successfully')
        router.reload()
        setDemographicsDialogOpen(false)
      } else {
        toast.error(data.errors?.join(', ') || 'Failed to update patient information')
      }
    } catch (error) {
      toast.error('An error occurred while updating patient information')
    } finally {
      setIsUpdatingDemographics(false)
    }
  }

  // Handler: Add Medication
  const handleAddMedication = async () => {
    setIsAddingMedication(true)
    try {
      const response = await fetch(`/admin/patients/${patient.id}/add_medication`, {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify({
          medication: {
            medication_name: newMedicationName,
            notes: newMedicationNotes,
          },
        }),
      })

      const data = await response.json()

      if (data.success) {
        toast.success('Medication added successfully')
        router.reload()
        setMedicationDialogOpen(false)
        setNewMedicationName('')
        setNewMedicationNotes('')
      } else {
        toast.error(data.errors?.join(', ') || 'Failed to add medication')
      }
    } catch (error) {
      toast.error('An error occurred while adding medication')
    } finally {
      setIsAddingMedication(false)
    }
  }

  // Handler: Remove Medication
  const handleRemoveMedication = async (medicationId: number) => {
    try {
      const response = await fetch(`/admin/patients/${patient.id}/remove_medication?medication_id=${medicationId}`, {
        method: 'DELETE',
        headers: getAuthHeaders(),
      })

      const data = await response.json()

      if (data.success) {
        toast.success('Medication removed successfully')
        router.reload()
      } else {
        toast.error(data.errors?.join(', ') || 'Failed to remove medication')
      }
    } catch (error) {
      toast.error('An error occurred while removing medication')
    }
  }

  // Handler: Update Treatment
  const handleUpdateTreatment = async () => {
    if (!treatmentRegimenName || !treatmentDurationLabel || !treatmentStartDate) {
      toast.error('Please fill in all required fields')
      return
    }

    setIsUpdatingTreatment(true)
    try {
      // First, update/create the treatment
      const treatmentResponse = await fetch(`/admin/patients/${patient.id}/update_treatment`, {
        method: 'PATCH',
        headers: getAuthHeaders(),
        body: JSON.stringify({
          treatment: {
            regimen_name: treatmentRegimenName,
            treatment_duration_label: treatmentDurationLabel,
            treatment_directions: treatmentDirections,
            treatment_start_date: treatmentStartDate,
            prior_auth_status: treatmentPriorAuthStatus,
          },
        }),
      })

      const treatmentData = await treatmentResponse.json()

      if (!treatmentData.success) {
        toast.error(treatmentData.errors?.join(', ') || 'Failed to update treatment')
        return
      }

      // If there are treatment plan instructions and a care case, save them
      if (care_case && treatmentPlanInstructions.trim()) {
        const isCreatePlan = !treatment_plan
        const planResponse = await fetch(
          isCreatePlan
            ? `/admin/patients/${patient.id}/create_treatment_plan`
            : `/admin/patients/${patient.id}/update_treatment_plan`,
          {
            method: isCreatePlan ? 'POST' : 'PATCH',
            headers: getAuthHeaders(),
            body: JSON.stringify({ instructions: treatmentPlanInstructions }),
          }
        )

        const planData = await planResponse.json()

        if (!planData.success) {
          toast.error(planData.errors?.join(', ') || 'Treatment saved but failed to update plan instructions')
          return
        }
      }

      toast.success('Treatment and plan updated successfully')
      setTreatmentDialogOpen(false)
      router.reload()
    } catch (error) {
      toast.error('An error occurred while updating treatment')
    } finally {
      setIsUpdatingTreatment(false)
    }
  }

  // Handler: Fetch ICD-10 codes for autocomplete
  const fetchIcd10Codes = async (query: string) => {
    if (query.length < 2) {
      setIcd10CodeOptions([])
      return
    }

    setIsLoadingCodes(true)
    try {
      const response = await fetch(`/admin/icd10_codes/autocomplete?q=${encodeURIComponent(query)}`, {
        headers: getAuthHeaders(),
      })

      if (response.ok) {
        const data = await response.json()
        setIcd10CodeOptions(data)
      }
    } catch (error) {
      console.error('Failed to fetch ICD-10 codes:', error)
    } finally {
      setIsLoadingCodes(false)
    }
  }

  // Handler: Add Diagnosis to Problem List
  const handleAddDiagnosis = async () => {
    // Validation based on mode
    if (useManualEntry) {
      if (!newDiagnosisIcd10Code.trim() || !newDiagnosisName.trim()) {
        toast.error('ICD-10 code and diagnosis name are required')
        return
      }
    } else {
      if (!selectedIcd10CodeId) {
        toast.error('Please select a diagnosis code')
        return
      }
    }

    setIsAddingDiagnosis(true)
    try {
      const body: any = {
        onset_date: newDiagnosisOnsetDate || null,
        notes: newDiagnosisNotes || null,
      }

      if (useManualEntry) {
        body.icd10_code = newDiagnosisIcd10Code
        body.diagnosis_name = newDiagnosisName
      } else {
        body.icd10_code_id = selectedIcd10CodeId
      }

      const response = await fetch(`/admin/patients/${patient.id}/add_problem_list_item`, {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify(body),
      })

      const data = await response.json()

      if (data.success) {
        toast.success('Diagnosis added to problem list')
        router.reload()
        setProblemListDialogOpen(false)
        setSelectedIcd10CodeId(null)
        setIcd10SearchQuery('')
        setNewDiagnosisIcd10Code('')
        setNewDiagnosisName('')
        setNewDiagnosisOnsetDate('')
        setNewDiagnosisNotes('')
        setUseManualEntry(false)
      } else {
        toast.error(data.errors?.join(', ') || 'Failed to add diagnosis')
      }
    } catch (error) {
      toast.error('An error occurred while adding diagnosis')
    } finally {
      setIsAddingDiagnosis(false)
    }
  }

  // Handler: Update Problem List Item Status
  const handleUpdateProblemListStatus = async (itemId: number, newStatus: string) => {
    try {
      const response = await fetch(`/admin/patients/${patient.id}/problem_list_items/${itemId}/update_status`, {
        method: 'PATCH',
        headers: getAuthHeaders(),
        body: JSON.stringify({
          status: newStatus,
        }),
      })

      const data = await response.json()

      if (data.success) {
        toast.success('Diagnosis status updated')
        router.reload()
      } else {
        toast.error(data.errors?.join(', ') || 'Failed to update status')
      }
    } catch (error) {
      toast.error('An error occurred while updating status')
    }
  }

  // Handler: Delete Problem List Item
  const handleDeleteProblemListItem = async (itemId: number) => {
    try {
      const response = await fetch(`/admin/patients/${patient.id}/problem_list_items/${itemId}`, {
        method: 'DELETE',
        headers: getAuthHeaders(),
      })

      const data = await response.json()

      if (data.success) {
        toast.success('Diagnosis removed from problem list')
        router.reload()
      } else {
        toast.error(data.errors?.join(', ') || 'Failed to remove diagnosis')
      }
    } catch (error) {
      toast.error('An error occurred while removing diagnosis')
    }
  }

  // Delete patient handler
  const handleDelete = () => {
    if (deleteConfirmText !== 'DELETE') {
      toast.error('Please type DELETE to confirm')
      return
    }

    router.delete(`/admin/patients/${patient.id}`, {
      onSuccess: () => {
        toast.success('Patient deleted successfully')
      },
      onError: () => {
        toast.error('Failed to delete patient')
      }
    })
  }

  // Helper: Get auth headers
  const getAuthHeaders = () => {
    const staffToken = localStorage.getItem('auth_token')
    const patientToken = localStorage.getItem('patient_session_token')
    const token = staffToken || patientToken

    return {
      'Content-Type': 'application/json',
      'X-CSRF-Token': document.querySelector<HTMLMetaElement>('meta[name="csrf-token"]')?.content || '',
      ...(token ? { 'Authorization': `Bearer ${token}` } : {}),
    }
  }

  // Helper: Get auth headers for FormData (without Content-Type)
  const getFormDataAuthHeaders = () => {
    const staffToken = localStorage.getItem('auth_token')
    const patientToken = localStorage.getItem('patient_session_token')
    const token = staffToken || patientToken

    return {
      'Accept': 'application/json',
      'X-CSRF-Token': document.querySelector<HTMLMetaElement>('meta[name="csrf-token"]')?.content || '',
      ...(token ? { 'Authorization': `Bearer ${token}` } : {}),
    }
  }

  // Helper: Format care stage label
  const formatCareStageLabel = (stage: string): string => {
    return stage.split('_').map(word => word.charAt(0).toUpperCase() + word.slice(1)).join(' ')
  }

  // Helper: Define care journey stages for progress visualization
  const getCareJourneyStages = () => {
    return [
      { key: 'intake', label: 'Intake', icon: ClipboardListIcon },
      { key: 'awaiting_testing', label: 'Testing', icon: FlaskConicalIcon },
      { key: 'awaiting_provider_intake', label: 'Provider Review', icon: UserIcon },
      { key: 'ready_for_tx', label: 'Ready for TX', icon: CheckCircle2Icon },
      { key: 'on_tx', label: 'On Treatment', icon: PillIcon },
      { key: 'svr12_pending', label: 'SVR12 Pending', icon: ActivityIcon },
      { key: 'cured', label: 'Cured', icon: HeartPulseIcon },
    ]
  }

  // Helper: Map all possible statuses to visual stages
  const mapStatusToVisualStage = (status: string): string => {
    const stageMapping: Record<string, string> = {
      // Early intake stages
      'referred': 'intake',
      'intake_scheduled': 'intake',
      'intake_rescheduled': 'intake',
      'intake': 'intake',

      // Testing/labs phase
      'awaiting_testing': 'awaiting_testing',
      'labs_pending': 'awaiting_testing',

      // Provider review phase
      'awaiting_provider_intake': 'awaiting_provider_intake',

      // Ready for treatment
      'ready_for_tx': 'ready_for_tx',

      // On treatment
      'on_tx': 'on_tx',
      'on_treatment': 'on_tx',

      // Post-treatment/SVR12 phase
      'treatment_completed_need_svr12': 'svr12_pending',
      'incomplete_treatment_need_svr12': 'svr12_pending',
      'svr12_pending': 'svr12_pending',

      // Final outcomes
      'cured': 'cured',

      // Special cases (map to closest equivalent)
      'treatment_incomplete': 'on_tx',
      'mia_unable_to_connect': 'intake',
      'closed_negative': 'intake',
    }

    return stageMapping[status] || 'intake'
  }

  // Helper: Get current stage index
  const getCurrentStageIndex = (status: string) => {
    const visualStage = mapStatusToVisualStage(status)
    const stages = getCareJourneyStages()
    const index = stages.findIndex(s => s.key === visualStage)
    return index >= 0 ? index : 0
  }

  // Helper: Calculate progress percentage
  const getProgressPercentage = (status: string) => {
    const stages = getCareJourneyStages()
    const currentIndex = getCurrentStageIndex(status)
    return ((currentIndex) / (stages.length - 1)) * 100
  }

  return (
    <div className="flex min-h-screen w-full flex-col bg-muted/40">
      <div className="flex flex-col sm:gap-4 sm:py-4">
        <AppHeader
          title={patient.display_name}
          breadcrumbs={[
            { label: 'Patients', href: '/admin/patients' },
            { label: patient.display_name },
          ]}
        />

        <main className="grid flex-1 items-start gap-6 p-4 sm:px-6 sm:py-0 md:gap-8">
          {/* Hero Section - Patient Summary */}
          <Card className="border-2 sticky top-0 z-10 bg-background">
            <CardContent className="pt-6">
              <div className="flex items-start justify-between gap-6">
                {/* Avatar and Basic Info */}
                <div className="flex items-start gap-4">
                  <div className="flex h-16 w-16 items-center justify-center rounded-full bg-gradient-to-br from-indigo-500 to-purple-600 text-2xl font-bold text-white">
                    {patient.display_name.split(' ').map(n => n[0]).join('').slice(0, 2)}
                  </div>
                  <div>
                    <h2 className="text-2xl font-bold">{patient.display_name}</h2>
                    <div className="mt-1 flex items-center gap-4 text-sm text-muted-foreground">
                      <span>{patient.age} years old</span>
                      <span>•</span>
                      <span className="flex items-center gap-1">
                        <PhoneIcon className="h-3 w-3" />
                        {patient.phone_number}
                      </span>
                      {patient.email && (
                        <>
                          <span>•</span>
                          <span>{patient.email}</span>
                        </>
                      )}
                    </div>
                    <div className="mt-2 flex items-center gap-2">
                      <Badge
                        variant={patient.consents_complete ? 'default' : 'destructive'}
                        className={`text-xs ${patient.consents_complete ? 'bg-green-100 dark:bg-green-900/30 text-green-800 dark:text-green-300 hover:bg-green-200 dark:hover:bg-green-900/50' : 'bg-red-100 dark:bg-red-900/30 text-red-800 dark:text-red-300 hover:bg-red-200 dark:hover:bg-red-900/50'}`}
                      >
                        {patient.consents_complete ? 'Consents Complete' : 'Consents Pending'}
                      </Badge>
                      <Badge variant="outline" className="text-xs">
                        <MapPinIcon className="mr-1 h-3 w-3" />
                        {patient.site_name}
                      </Badge>
                      {care_case && (
                        <>
                          <Badge
                            variant="outline"
                            className={`text-xs cursor-pointer transition-all ${!care_case.treatment_status ? 'border-dashed border-gray-400 text-gray-600 hover:border-gray-600' : ''}`}
                            onClick={() => {
                              setSelectedTreatmentStatus(care_case.treatment_status || '')
                              setTreatmentStatusDialogOpen(true)
                            }}
                          >
                            {care_case.treatment_status_label || 'Set Treatment Status'}
                          </Badge>
                          <Badge
                            variant={care_case.priority === 'urgent' ? 'destructive' : care_case.priority === 'high' ? 'default' : 'secondary'}
                            className="text-xs cursor-pointer transition-all"
                            onClick={() => {
                              setSelectedPriority(care_case.priority)
                              setPriorityDialogOpen(true)
                            }}
                          >
                            {care_case.priority_label} Priority
                          </Badge>
                        </>
                      )}
                      {risk_assessment && (
                        <Badge
                          variant={getRiskBadgeVariant(risk_assessment.risk_level_label)}
                          className={`text-xs cursor-pointer transition-all ${getRiskBadgeColorClasses(risk_assessment.risk_level_label)}`}
                          onClick={() => setQuestionnaireModalOpen(true)}
                        >
                          <AlertCircleIcon className="mr-1 h-3 w-3" />
                          {risk_assessment.risk_level_label} Risk
                        </Badge>
                      )}
                    </div>
                  </div>
                </div>

                {/* Days at Current Stage */}
                {care_case && (
                  <div className="flex flex-col items-end gap-2">
                    <div className="text-right">
                      <div className="text-xs text-muted-foreground">Days at Current Stage</div>
                      <div
                        className={`text-4xl font-bold ${
                          care_case.days_since_last_activity <= 3
                            ? 'text-green-600 dark:text-green-400'
                            : care_case.days_since_last_activity <= 7
                            ? 'text-yellow-600 dark:text-yellow-400'
                            : 'text-red-600 dark:text-red-400'
                        }`}
                      >
                        {care_case.days_since_last_activity}
                      </div>
                    </div>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => router.visit('/admin/patients')}
                    >
                      <ArrowLeftIcon className="h-4 w-4" />
                    </Button>
                  </div>
                )}
              </div>
            </CardContent>
          </Card>

          {/* Care Journey Progress Bar */}
          {care_case && (
            <Card>
              <CardHeader>
                <div className="flex items-center justify-between">
                  <CardTitle className="flex items-center gap-2">
                    <HeartPulseIcon className="h-5 w-5" />
                    Care Journey
                  </CardTitle>
                  <span className="text-sm text-muted-foreground">{care_case.days_open} days open</span>
                </div>
              </CardHeader>
              <CardContent className="space-y-6">
                {/* Progress Bar with Stages */}
                <div className="relative">
                  {/* Progress Line */}
                  <div className="absolute left-0 right-0 top-6 h-1 bg-muted">
                    <div
                      className="h-full bg-gradient-to-r from-indigo-500 to-purple-600 transition-all duration-500"
                      style={{ width: `${getProgressPercentage(care_case.case_status)}%` }}
                    />
                  </div>

                  {/* Stage Markers */}
                  <div className="relative flex justify-between">
                    {getCareJourneyStages().map((stage, index) => {
                      const currentIndex = getCurrentStageIndex(care_case.case_status)
                      const isCompleted = index < currentIndex
                      const isCurrent = index === currentIndex
                      const StageIcon = stage.icon

                      return (
                        <div key={stage.key} className="flex flex-col items-center gap-2" style={{ width: '14%' }}>
                          {/* Stage Icon/Circle */}
                          <div
                            className={`relative z-10 flex h-12 w-12 items-center justify-center rounded-full border-2 transition-all ${
                              isCompleted
                                ? 'border-indigo-500 bg-indigo-500 text-white'
                                : isCurrent
                                ? 'animate-pulse border-indigo-500 bg-white text-indigo-500 ring-4 ring-indigo-500/20 dark:bg-slate-950'
                                : 'border-muted bg-white text-muted-foreground dark:bg-slate-950'
                            }`}
                          >
                            {isCompleted ? (
                              <CheckCircle2Icon className="h-6 w-6" />
                            ) : (
                              <StageIcon className="h-5 w-5" />
                            )}
                          </div>

                          {/* Stage Label */}
                          <div className="text-center">
                            <div
                              className={`text-xs font-medium ${
                                isCurrent ? 'text-indigo-600 dark:text-indigo-400' : 'text-muted-foreground'
                              }`}
                            >
                              {stage.label}
                            </div>
                          </div>
                        </div>
                      )
                    })}
                  </div>
                </div>

                {/* Current Status and Actions */}
                <div className="flex items-center justify-between rounded-lg border bg-muted/50 p-4">
                  <div>
                    <div className="text-sm font-medium text-muted-foreground">Current Stage</div>
                    <Badge
                      variant="default"
                      className="mt-1 cursor-pointer"
                      onClick={() => {
                        setSelectedCareStage(care_case.case_status)
                        setCareStageDialogOpen(true)
                      }}
                    >
                      {care_case.status_label}
                    </Badge>
                  </div>
                  {care_case.treatment_status && (
                    <div className="text-center">
                      <div className="text-sm font-medium text-muted-foreground">Treatment Status</div>
                      <Badge variant="outline" className="mt-1">
                        {care_case.treatment_status_label}
                      </Badge>
                    </div>
                  )}
                  <div className="text-right">
                    <div className="text-sm font-medium text-muted-foreground">Last Updated</div>
                    <div className="mt-1 text-sm">
                      {care_case.days_since_last_activity === 0
                        ? 'Today'
                        : `${care_case.days_since_last_activity} days ago`}
                    </div>
                  </div>
                </div>

                {/* Status History - Collapsible */}
                <details className="group">
                  <summary className="flex cursor-pointer items-center justify-between text-sm font-medium text-muted-foreground hover:text-foreground">
                    <span>Status History ({care_case.status_history.length} changes)</span>
                    <span className="transition-transform group-open:rotate-90">›</span>
                  </summary>
                  <div className="mt-4 space-y-2 border-l-2 border-muted pl-4">
                    {care_case.status_history.map((entry, index) => (
                      <div key={index} className="relative">
                        <div className="absolute -left-[1.3rem] top-1.5 h-2 w-2 rounded-full bg-indigo-500" />
                        <div className="text-sm">
                          <span className="font-medium">{entry.status_label}</span>
                          <span className="ml-2 text-xs text-muted-foreground">
                            {new Date(entry.changed_at).toLocaleDateString()} at{' '}
                            {new Date(entry.changed_at).toLocaleTimeString([], {
                              hour: '2-digit',
                              minute: '2-digit',
                            })}
                          </span>
                        </div>
                      </div>
                    ))}
                  </div>
                </details>

                {care_case.cured && (
                  <div className="rounded-lg border-2 border-green-500 bg-green-50 p-4 dark:bg-green-950">
                    <div className="flex items-center gap-2 text-sm font-medium text-green-900 dark:text-green-100">
                      <CheckCircle2Icon className="h-5 w-5" />
                      🎉 Patient Cured!
                    </div>
                    <div className="mt-1 text-sm text-green-700 dark:text-green-300">
                      {care_case.closed_reason}
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>
          )}

          {/* Assessment Required Banner - shown when no risk assessment exists */}
          {!risk_assessment && questionnaire_questions.length > 0 && (
            <div className="rounded-xl border-2 border-orange-400 dark:border-orange-600 bg-gradient-to-r from-orange-50 to-amber-50 dark:from-orange-950/40 dark:to-amber-950/40 p-5 shadow-sm">
              <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
                <div className="flex items-start gap-3">
                  <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-orange-100 dark:bg-orange-900/50">
                    <AlertCircleIcon className="h-5 w-5 text-orange-600 dark:text-orange-400" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-orange-900 dark:text-orange-200">HCV Risk Assessment Required</h3>
                    <p className="mt-0.5 text-sm text-orange-700 dark:text-orange-300">
                      No risk assessment has been completed for this patient yet. Complete the intake questionnaire to determine their HCV risk score and open a care case.
                    </p>
                  </div>
                </div>
                <Button
                  onClick={() => setAssessmentIntakeOpen(true)}
                  className="shrink-0 bg-orange-600 hover:bg-orange-700 text-white shadow-sm"
                >
                  <ClipboardListIcon className="mr-2 h-4 w-4" />
                  Start Risk Assessment
                </Button>
              </div>
            </div>
          )}

          {/* Patient Information - Full Width */}
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <CardTitle className="flex items-center gap-2">
                  <UserIcon className="h-5 w-5" />
                  Patient Information
                </CardTitle>
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => {
                    setHomeAddress(patient.home_address || '')
                    setAllergies(patient.allergies || '')
                    setSexAssignedAtBirth(patient.sex_assigned_at_birth || '')
                    setDemographicsDialogOpen(true)
                  }}
                >
                  Edit
                </Button>
              </div>
            </CardHeader>
            <CardContent>
              <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
                {/* Basic Demographics */}
                <div className="space-y-4">
                  <div>
                    <div className="text-sm font-medium text-muted-foreground">Legal Name</div>
                    <div className="text-sm font-semibold">{patient.legal_name}</div>
                  </div>

                  {patient.alias_name && (
                    <div>
                      <div className="text-sm font-medium text-muted-foreground">Alias Name</div>
                      <div className="text-sm">{patient.alias_name}</div>
                    </div>
                  )}

                  {patient.age && (
                    <div>
                      <div className="text-sm font-medium text-muted-foreground">Age</div>
                      <div className="text-sm">{patient.age} years</div>
                    </div>
                  )}

                  {patient.sex_assigned_at_birth && (
                    <div>
                      <div className="text-sm font-medium text-muted-foreground">Sex Assigned at Birth</div>
                      <div className="text-sm">{patient.sex_assigned_at_birth}</div>
                    </div>
                  )}
                </div>

                {/* Contact Information */}
                <div className="space-y-4">
                  <div>
                    <div className="text-sm font-medium text-muted-foreground">Phone</div>
                    <div className="text-sm flex items-center gap-2">
                      <PhoneIcon className="h-3 w-3" />
                      {patient.phone_number}
                    </div>
                  </div>

                  {patient.email && (
                    <div>
                      <div className="text-sm font-medium text-muted-foreground">Email</div>
                      <div className="text-sm">{patient.email}</div>
                    </div>
                  )}

                  {patient.zip_code && (
                    <div>
                      <div className="text-sm font-medium text-muted-foreground">ZIP Code</div>
                      <div className="text-sm">{patient.zip_code}</div>
                    </div>
                  )}

                  {patient.home_address && (
                    <div>
                      <div className="text-sm font-medium text-muted-foreground">Home Address</div>
                      <div className="text-sm whitespace-pre-wrap">{patient.home_address}</div>
                    </div>
                  )}
                </div>

                {/* Administrative & Additional Info */}
                <div className="space-y-4">
                  {patient.allergies && (
                    <div>
                      <div className="text-sm font-medium text-muted-foreground">Allergies</div>
                      <div className="text-sm whitespace-pre-wrap">{patient.allergies}</div>
                    </div>
                  )}

                  <div>
                    <div className="text-sm font-medium text-muted-foreground">Site</div>
                    <div className="text-sm">{patient.site_name}</div>
                  </div>

                  <div>
                    <div className="text-sm font-medium text-muted-foreground">Consents</div>
                    <Badge variant={patient.consents_complete ? 'default' : 'destructive'}>
                      {patient.consents_complete ? 'Complete' : 'Incomplete'}
                    </Badge>
                  </div>
                </div>
              </div>

              {/* Emergency Contacts - Full Width Row */}
              {patient.emergency_contacts.length > 0 && (
                <>
                  <Separator className="my-6" />
                  <div>
                    <div className="text-sm font-medium text-muted-foreground mb-4">Emergency Contacts</div>
                    <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
                      {patient.emergency_contacts.map((contact, index) => (
                        <div key={index} className="rounded-lg border bg-muted/50 p-3">
                          <div className="text-sm font-medium">{contact.name}</div>
                          <div className="text-sm text-muted-foreground flex items-center gap-2 mt-1">
                            <PhoneIcon className="h-3 w-3" />
                            {contact.phone}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                </>
              )}
            </CardContent>
          </Card>

          {/* Two-Column Grid */}
          <div className="grid gap-6 md:grid-cols-2">
            {/* LEFT COLUMN - Clinical Data */}
            <div className="space-y-6">
            {/* Risk Assessment - Counselor Notes */}
              {risk_assessment && risk_assessment.counselor_summary && (
                <Card>
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                      <AlertCircleIcon className="h-5 w-5" />
                      Risk Assessment Notes
                    </CardTitle>
                    <CardDescription>
                      Completed {new Date(risk_assessment.completed_at).toLocaleDateString()}
                    </CardDescription>
                  </CardHeader>
                  <CardContent>
                    <div className="rounded-lg border bg-muted/50 p-3">
                      <div className="text-sm font-medium text-muted-foreground mb-2">Counselor Summary</div>
                      <div className="text-sm leading-relaxed">{risk_assessment.counselor_summary}</div>
                    </div>
                  </CardContent>
                </Card>
              )}
            </div>

            {/* RIGHT COLUMN - Administrative & Contact Info (Empty - all moved to full width) */}
            <div className="space-y-6">
              {/* All content moved to full-width sections below */}
            </div>
          </div>

          {/* Appointments - Full Width */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <CalendarIcon className="h-5 w-5 text-purple-600 dark:text-purple-400" />
                  Appointments
                </CardTitle>
                <CardDescription>
                  {appointments.upcoming.length} upcoming, {appointments.past.length} past
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-6">
                {/* Upcoming Appointments */}
                <div>
                  <h3 className="text-sm font-semibold text-foreground mb-3 flex items-center gap-2">
                    <CalendarIcon className="h-4 w-4 text-purple-600 dark:text-purple-400" />
                    Upcoming Appointments
                  </h3>
                  {appointments.upcoming.length > 0 ? (
                    <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
                      {appointments.upcoming.map((appointment) => (
                        <div
                          key={appointment.id}
                          className="group relative overflow-hidden rounded-lg border-2 border-purple-200 dark:border-purple-900/50 bg-purple-50 dark:bg-purple-900/20 p-4 transition-all hover:shadow-md cursor-pointer"
                          onClick={() => router.visit(`/admin/appointments/${appointment.id}`)}
                        >
                          <div className="flex flex-col gap-3">
                            <div className="flex items-start justify-between gap-2">
                              <Badge className="bg-purple-100 text-purple-800 dark:bg-purple-900/30 dark:text-purple-300">
                                {appointment.type_label}
                              </Badge>
                              <Badge variant={appointment.status === 'confirmed' ? 'default' : 'outline'}>
                                {appointment.status_label}
                              </Badge>
                            </div>

                            <div className="space-y-2">
                              <div className="flex items-center gap-2 text-sm">
                                <CalendarIcon className="h-4 w-4 text-muted-foreground" />
                                <span className="font-medium text-foreground">{appointment.formatted_datetime}</span>
                              </div>
                              <div className="flex items-center gap-2 text-sm text-muted-foreground">
                                <span>Duration: {appointment.duration_minutes} min</span>
                              </div>
                              {appointment.location && (
                                <div className="flex items-center gap-2 text-sm">
                                  <MapPinIcon className="h-4 w-4 text-muted-foreground" />
                                  <span className="text-muted-foreground">{appointment.location}</span>
                                </div>
                              )}
                              {appointment.reason && (
                                <div className="text-sm text-muted-foreground mt-2 pt-2 border-t border-purple-200 dark:border-purple-900/50">
                                  {appointment.reason}
                                </div>
                              )}
                              <div className="text-xs text-muted-foreground">
                                Site: {appointment.site_name}
                              </div>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className="text-center py-4 text-sm text-muted-foreground">
                      No upcoming appointments scheduled
                    </div>
                  )}
                </div>

                {/* Past Appointments */}
                <div>
                  <h3 className="text-sm font-semibold text-foreground mb-3">Past Appointments</h3>
                  {appointments.past.length > 0 ? (
                    <div className="space-y-2">
                      {appointments.past.map((appointment) => (
                        <div
                          key={appointment.id}
                          className="flex items-center justify-between rounded-lg border bg-muted/50 p-3 hover:bg-muted cursor-pointer transition-colors"
                          onClick={() => router.visit(`/admin/appointments/${appointment.id}`)}
                        >
                          <div className="flex items-center gap-4 flex-1">
                            <div className="flex flex-col gap-1 flex-1">
                              <div className="flex items-center gap-2">
                                <span className="text-sm font-medium text-foreground">{appointment.type_label}</span>
                                <Badge
                                  variant={
                                    appointment.status === 'completed'
                                      ? 'default'
                                      : 'destructive'
                                  }
                                  className="text-xs"
                                >
                                  {appointment.status_label}
                                </Badge>
                              </div>
                              <div className="flex items-center gap-3 text-xs text-muted-foreground">
                                <span>{appointment.formatted_date} at {appointment.formatted_time}</span>
                                {appointment.location && (
                                  <>
                                    <span>•</span>
                                    <span>{appointment.location}</span>
                                  </>
                                )}
                              </div>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className="text-center py-4 text-sm text-muted-foreground">
                      No past appointments on record
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>

          {/* Treatment Information - Full Width */}
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle className="flex items-center gap-2">
                    <PillIcon className="h-5 w-5 text-purple-600 dark:text-purple-400" />
                    Treatment Information
                  </CardTitle>
                  <CardDescription>
                    {treatment ? 'Current HCV treatment regimen' : 'No active treatment'}
                  </CardDescription>
                </div>
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => {
                    setTreatmentRegimenName(treatment?.regimen_name || '')
                    setTreatmentDurationLabel(treatment?.treatment_duration_label || '')
                    setTreatmentDirections(treatment?.treatment_directions || '')
                    setTreatmentStartDate(treatment?.treatment_start_date || '')
                    setTreatmentPriorAuthStatus(treatment?.prior_auth_status || '')
                    setTreatmentPlanInstructions(treatment_plan?.instructions || '')
                    setTreatmentDialogOpen(true)
                  }}
                >
                  {treatment ? 'Edit' : 'Add Treatment'}
                </Button>
              </div>
            </CardHeader>
            <CardContent>
              {treatment ? (
                <div className="space-y-4">
                  {/* Medication Regimen */}
                  <div className="rounded-lg bg-purple-500/10 dark:bg-purple-500/20 p-4">
                    <p className="text-sm font-medium text-purple-900 dark:text-purple-200">Medication</p>
                    <p className="mt-1 text-base font-semibold text-purple-700 dark:text-purple-300">{treatment.regimen_name}</p>
                  </div>

                  {/* Treatment Directions */}
                  {treatment.treatment_directions && (
                    <div className="rounded-lg border border-purple-200 dark:border-purple-800 bg-purple-50/50 dark:bg-purple-950/20 p-4">
                      <p className="text-sm font-medium text-purple-900 dark:text-purple-200">How to Take</p>
                      <p className="mt-1 text-sm text-purple-800 dark:text-purple-300">{treatment.treatment_directions}</p>
                    </div>
                  )}

                  {/* Treatment Stats Grid */}
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <p className="text-sm font-medium text-muted-foreground">Duration</p>
                      <p className="text-lg font-semibold text-foreground">
                        {treatment.treatment_duration_label || `${treatment.duration_weeks} weeks`}
                      </p>
                    </div>
                    <div>
                      <p className="text-sm font-medium text-muted-foreground">Status</p>
                      <Badge className={treatment.active ? "bg-green-500/20 text-green-900 dark:text-green-200" : "bg-blue-500/20 text-blue-900 dark:text-blue-200"}>
                        {treatment.status_label}
                      </Badge>
                    </div>
                  </div>

                  {/* Treatment Dates */}
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <p className="text-sm font-medium text-muted-foreground">Start Date</p>
                      <p className="text-sm text-foreground">{new Date(treatment.treatment_start_date).toLocaleDateString()}</p>
                    </div>
                    <div>
                      <p className="text-sm font-medium text-muted-foreground">Expected End</p>
                      <p className="text-sm text-foreground">{new Date(treatment.treatment_end_date).toLocaleDateString()}</p>
                    </div>
                  </div>

                  {/* Treatment Progress */}
                  {treatment.active && (
                    <div>
                      <div className="flex items-center justify-between mb-2">
                        <p className="text-sm font-medium text-muted-foreground">Progress</p>
                        <p className="text-sm font-semibold text-foreground">{treatment.completion_percentage}%</p>
                      </div>
                      <div className="w-full bg-muted rounded-full h-2.5">
                        <div
                          className="bg-gradient-to-r from-purple-500 to-indigo-500 h-2.5 rounded-full transition-all"
                          style={{ width: `${treatment.completion_percentage}%` }}
                        />
                      </div>
                      <p className="text-xs text-muted-foreground mt-2">
                        Week {treatment.weeks_elapsed + 1} of {treatment.duration_weeks} • {treatment.days_remaining} days remaining
                      </p>
                    </div>
                  )}

                  {/* Prior Authorization Status */}
                  {treatment.prior_auth_status && (
                    <div className="flex items-center justify-between rounded-lg border p-3">
                      <span className="text-sm font-medium">Prior Authorization</span>
                      <Badge
                        className={
                          treatment.prior_auth_status === 'approved' || treatment.prior_auth_status === 'not_required'
                            ? 'bg-green-500/20 text-green-900 dark:text-green-200'
                            : treatment.prior_auth_status === 'pending'
                            ? 'bg-yellow-500/20 text-yellow-900 dark:text-yellow-200'
                            : 'bg-red-500/20 text-red-900 dark:text-red-200'
                        }
                      >
                        {treatment.prior_auth_status_label}
                      </Badge>
                    </div>
                  )}

                  {/* Treatment Plan Instructions - Display Only */}
                  {care_case && treatment_plan && (
                    <>
                      <Separator className="my-6" />
                      <div className="space-y-3">
                        <div className="flex items-center gap-2">
                          <FileTextIcon className="h-4 w-4 text-purple-600 dark:text-purple-400" />
                          <span className="text-sm font-semibold">Treatment Plan Instructions</span>
                        </div>
                        <div className="p-3 bg-purple-50/50 dark:bg-purple-950/20 border border-purple-200 dark:border-purple-800 rounded-lg whitespace-pre-wrap text-sm">
                          {treatment_plan.instructions}
                        </div>
                        <div className="text-xs text-muted-foreground">
                          Created by {treatment_plan.created_by_name} • Last updated by {treatment_plan.last_updated_by_name}
                        </div>
                      </div>
                    </>
                  )}
                </div>
              ) : (
                <div className="text-center py-8">
                  <PillIcon className="h-12 w-12 text-muted-foreground/40 mx-auto mb-3" />
                  <p className="text-sm font-medium text-muted-foreground">No active treatment</p>
                  <p className="text-xs text-muted-foreground mt-1">Patient has not started HCV treatment yet</p>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Active Medications - Full Width */}
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle className="flex items-center gap-2">
                    <PillIcon className="h-5 w-5" />
                    Active Medications
                  </CardTitle>
                  <CardDescription>{active_medications.length} medications</CardDescription>
                </div>
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => {
                    setNewMedicationName('')
                    setNewMedicationNotes('')
                    setMedicationDialogOpen(true)
                  }}
                >
                  <PlusIcon className="h-4 w-4 mr-1" />
                  Add Medication
                </Button>
              </div>
            </CardHeader>
            <CardContent>
              {active_medications.length > 0 ? (
                <div className="space-y-2">
                  {active_medications.map((med) => (
                    <div key={med.id} className="flex items-center justify-between rounded-lg border bg-muted/50 p-3">
                      <div className="flex-1">
                        <div className="font-medium">{med.medication_name}</div>
                        {med.notes && (
                          <div className="text-sm text-muted-foreground mt-1">{med.notes}</div>
                        )}
                      </div>
                      <Button
                        size="sm"
                        variant="ghost"
                        className="text-destructive hover:text-destructive"
                        onClick={() => handleRemoveMedication(med.id)}
                      >
                        Remove
                      </Button>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-6 text-sm text-muted-foreground">
                  No active medications
                </div>
              )}
            </CardContent>
          </Card>

          {/* Lab Orders - Full Width */}
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle className="flex items-center gap-2">
                    <FlaskConicalIcon className="h-5 w-5" />
                    Laboratory Tests
                  </CardTitle>
                  <CardDescription>
                    {lab_orders.length > 0
                      ? `${lab_orders.length} test${lab_orders.length === 1 ? '' : 's'} ordered`
                      : 'No lab orders yet'}
                  </CardDescription>
                </div>
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => setCreateLabOrderDialogOpen(true)}
                >
                  <PlusIcon className="mr-1 h-4 w-4" />
                  Create Lab Order
                </Button>
              </div>
            </CardHeader>
            <CardContent>
              {lab_orders.length > 0 ? (
                <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
                  {lab_orders.map((lab) => (
                    <div key={lab.id} className="group relative overflow-hidden rounded-lg border bg-muted/50 p-4 transition-all hover:shadow-md">
                      <div className="flex items-start justify-between gap-2">
                        <div className="flex-1">
                          <div className="flex items-center gap-2">
                            <FlaskConicalIcon className="h-4 w-4 text-muted-foreground" />
                            <div className="font-medium">{lab.order_type_label}</div>
                          </div>
                          <div className="mt-2 text-xs text-muted-foreground">
                            {new Date(lab.ordered_at).toLocaleDateString()}
                          </div>
                          {lab.result_value && (
                            <div className="mt-2 rounded bg-background p-2 text-sm">
                              <div className="font-medium">Result:</div>
                              <div className="text-muted-foreground">{lab.result_value}</div>
                            </div>
                          )}
                          {lab.result_notes && (
                            <div className="mt-2 rounded bg-background p-2 text-sm">
                              <div className="font-medium">Notes:</div>
                              <div className="text-muted-foreground text-xs">{lab.result_notes}</div>
                            </div>
                          )}
                          {lab.result_files && lab.result_files.length > 0 && (
                            <div className="mt-2 space-y-1">
                              <div className="font-medium text-xs">Attached Files:</div>
                              {lab.result_files.map((file: any) => (
                                <button
                                  key={file.id}
                                  onClick={() => {
                                    setPreviewFile(file)
                                    setFilePreviewOpen(true)
                                  }}
                                  className="flex items-center gap-1 text-xs text-primary hover:underline"
                                >
                                  <FileTextIcon className="h-3 w-3" />
                                  {file.filename}
                                </button>
                              ))}
                            </div>
                          )}
                        </div>
                        <Badge variant={lab.status === 'result_received' ? 'default' : 'secondary'} className="text-xs">
                          {lab.status_label}
                        </Badge>
                      </div>
                      {lab.status !== 'result_received' && (
                        <Button
                          size="sm"
                          variant="outline"
                          className="mt-3 w-full"
                          onClick={() => {
                            setSelectedLabOrder(lab)
                            setLabResultFile(null)
                            setLabResultValue('')
                            setLabResultNotes('')
                            setLabUploadDialogOpen(true)
                          }}
                        >
                          <UploadIcon className="mr-1 h-3 w-3" />
                          Upload Result
                        </Button>
                      )}
                    </div>
                  ))}
                </div>
              ) : (
                <div className="flex flex-col items-center justify-center py-12 text-center">
                  <div className="rounded-full bg-muted p-4 mb-4">
                    <FlaskConicalIcon className="h-8 w-8 text-muted-foreground" />
                  </div>
                  <p className="text-sm font-medium text-muted-foreground">No lab orders yet</p>
                  <p className="text-xs text-muted-foreground mt-1">Lab orders will appear here once created</p>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Case Notes */}
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle className="flex items-center gap-2">
                    <FileTextIcon className="h-5 w-5" />
                    Case Notes
                  </CardTitle>
                  <CardDescription>{case_notes.length} internal notes</CardDescription>
                </div>
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => {
                    setCaseNoteBody('')
                    setCaseNoteType('general')
                    setCaseNoteDialogOpen(true)
                  }}
                >
                  <PlusIcon className="h-4 w-4 mr-1" />
                  Add Note
                </Button>
              </div>
            </CardHeader>
            <CardContent>
              {case_notes.length > 0 ? (
                <div className="space-y-4">
                  {case_notes.map((note) => (
                    <div key={note.id} className="p-3 border rounded-lg">
                      <div className="flex items-center justify-between mb-2">
                        <Badge variant="outline">{note.note_type_label}</Badge>
                        <span className="text-xs text-muted-foreground">
                          {new Date(note.created_at).toLocaleString()}
                        </span>
                      </div>
                      <div className="text-sm text-muted-foreground mb-2">{note.body}</div>
                      <div className="text-xs text-muted-foreground">— {note.author_name}</div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-sm text-muted-foreground">No case notes yet. Click "Add Note" to create one.</div>
              )}
            </CardContent>
          </Card>

          {/* Problem List */}
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle className="flex items-center gap-2">
                    <ClipboardIcon className="h-5 w-5" />
                    Problem List
                  </CardTitle>
                  <CardDescription>{problem_list_items.length} diagnoses</CardDescription>
                </div>
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => {
                    setNewDiagnosisIcd10Code('')
                    setNewDiagnosisName('')
                    setNewDiagnosisOnsetDate('')
                    setNewDiagnosisNotes('')
                    setProblemListDialogOpen(true)
                  }}
                >
                  <PlusIcon className="h-4 w-4 mr-1" />
                  Add Diagnosis
                </Button>
              </div>
            </CardHeader>
            <CardContent>
              {problem_list_items.length > 0 ? (
                <div className="space-y-2">
                  {problem_list_items.map((item) => (
                    <div key={item.id} className="rounded-lg border bg-muted/50 p-3">
                      <div className="flex items-start justify-between">
                        <div className="flex-1">
                          <div className="flex items-center gap-2">
                            <span className="font-mono text-sm font-medium">{item.icd10_code}</span>
                            <Badge variant={item.status === 'active' ? 'default' : item.status === 'resolved' ? 'secondary' : 'outline'}>
                              {item.status}
                            </Badge>
                          </div>
                          <div className="font-medium mt-1">{item.diagnosis_name}</div>
                          {item.onset_date && (
                            <div className="text-sm text-muted-foreground mt-1">
                              Onset: {new Date(item.onset_date).toLocaleDateString()}
                            </div>
                          )}
                          {item.notes && (
                            <div className="text-sm text-muted-foreground mt-1">{item.notes}</div>
                          )}
                          <div className="text-xs text-muted-foreground mt-2">
                            Added by {item.creator_name} on {new Date(item.created_at).toLocaleDateString()}
                          </div>
                        </div>
                        <div className="flex gap-1 ml-2">
                          {item.status === 'active' && (
                            <Button
                              size="sm"
                              variant="ghost"
                              onClick={() => handleUpdateProblemListStatus(item.id, 'resolved')}
                              title="Mark as resolved"
                            >
                              <CheckCircle2Icon className="h-4 w-4" />
                            </Button>
                          )}
                          <Button
                            size="sm"
                            variant="ghost"
                            className="text-destructive hover:text-destructive"
                            onClick={() => handleDeleteProblemListItem(item.id)}
                            title="Remove from problem list"
                          >
                            <XIcon className="h-4 w-4" />
                          </Button>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-6 text-sm text-muted-foreground">
                  No diagnoses in problem list
                </div>
              )}
            </CardContent>
          </Card>

          {/* Insurance & Prescription - Full Width */}
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle className="flex items-center gap-2">
                    <CreditCardIcon className="h-5 w-5" />
                    Insurance & Prescription
                  </CardTitle>
                  <CardDescription>
                    {patient.prescription.plan_name ? 'Prescription coverage information' : 'No insurance information on file'}
                  </CardDescription>
                </div>
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => {
                    setPrescriptionPlanName(patient.prescription.plan_name || '')
                    setPrescriptionIdNumber(patient.prescription.id_number || '')
                    setPrescriptionBin(patient.prescription.bin || '')
                    setPrescriptionGroupNumber(patient.prescription.group_number || '')
                    setPrescriptionPcn(patient.prescription.pcn || '')
                    setPrescriptionDialogOpen(true)
                  }}
                >
                  Edit
                </Button>
              </div>
            </CardHeader>
            <CardContent>
              {patient.prescription.plan_name ? (
                <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4">
                  <div>
                    <div className="text-sm font-medium text-muted-foreground">Health Plan Name</div>
                    <div className="text-sm font-semibold">{patient.prescription.plan_name}</div>
                  </div>
                  <div>
                    <div className="text-sm font-medium text-muted-foreground">ID Number</div>
                    <div className="text-sm font-semibold">{patient.prescription.id_number}</div>
                  </div>
                  <div>
                    <div className="text-sm font-medium text-muted-foreground">BIN</div>
                    <div className="text-sm font-semibold">{patient.prescription.bin}</div>
                  </div>
                  <div>
                    <div className="text-sm font-medium text-muted-foreground">Group Number</div>
                    <div className="text-sm font-semibold">{patient.prescription.group_number}</div>
                  </div>
                  <div>
                    <div className="text-sm font-medium text-muted-foreground">PCN</div>
                    <div className="text-sm font-semibold">{patient.prescription.pcn}</div>
                  </div>
                </div>
              ) : (
                <div className="text-center py-8">
                  <CreditCardIcon className="h-12 w-12 text-muted-foreground/40 mx-auto mb-3" />
                  <p className="text-sm font-medium text-muted-foreground">No prescription information on file</p>
                  <p className="text-xs text-muted-foreground mt-1">Click "Edit" to add insurance details</p>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Action Buttons */}
          <div className="flex justify-between">
            <Button variant="outline" onClick={() => router.visit('/admin/patients')}>
              <ArrowLeftIcon className="mr-2 h-4 w-4" />
              Back to Patients
            </Button>
            <Button
              variant="outline"
              className="border-red-200 text-red-600 hover:bg-red-50 dark:border-red-900 dark:text-red-400"
              onClick={() => setShowDeleteDialog(true)}
            >
              <Trash2Icon className="mr-2 h-4 w-4" />
              Delete Patient
            </Button>
          </div>
        </main>

        {/* Priority Update Dialog */}
        <Dialog open={priorityDialogOpen} onOpenChange={setPriorityDialogOpen}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Update Priority</DialogTitle>
              <DialogDescription>
                Change the priority level for this patient's care case.
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-4 py-4">
              <div className="space-y-2">
                <Label>Priority Level</Label>
                <Select value={selectedPriority} onValueChange={setSelectedPriority}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="urgent">Urgent (Red)</SelectItem>
                    <SelectItem value="high">High (Orange)</SelectItem>
                    <SelectItem value="standard">Standard (Blue)</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setPriorityDialogOpen(false)}>
                Cancel
              </Button>
              <Button onClick={handleUpdatePriority} disabled={isUpdatingPriority}>
                {isUpdatingPriority ? 'Updating...' : 'Update Priority'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Treatment Status Update Dialog */}
        <Dialog open={treatmentStatusDialogOpen} onOpenChange={setTreatmentStatusDialogOpen}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Update Treatment Status</DialogTitle>
              <DialogDescription>
                Change the treatment status for this patient.
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-4 py-4">
              <div className="space-y-2">
                <Label>Treatment Status</Label>
                <Select value={selectedTreatmentStatus} onValueChange={setSelectedTreatmentStatus}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="treatment_naive">Treatment Naive</SelectItem>
                    <SelectItem value="treatment_experienced">Treatment Experienced</SelectItem>
                    <SelectItem value="treatment_naive_reinfection">Treatment Naive, by reinfection</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setTreatmentStatusDialogOpen(false)}>
                Cancel
              </Button>
              <Button onClick={handleUpdateTreatmentStatus} disabled={isUpdatingTreatmentStatus}>
                {isUpdatingTreatmentStatus ? 'Updating...' : 'Update Status'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Case Status Update Dialog */}
        <Dialog open={caseStatusDialogOpen} onOpenChange={setCaseStatusDialogOpen}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Update Case Status</DialogTitle>
              <DialogDescription>
                Change the status for this patient's care case.
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-4 py-4">
              <div className="space-y-2">
                <Label>Case Status</Label>
                <Select value={selectedCaseStatus} onValueChange={setSelectedCaseStatus}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="referred">Referred</SelectItem>
                    <SelectItem value="intake_scheduled">Intake Scheduled</SelectItem>
                    <SelectItem value="intake_rescheduled">Intake Rescheduled</SelectItem>
                    <SelectItem value="labs_pending">Labs Pending</SelectItem>
                    <SelectItem value="on_treatment">On Treatment</SelectItem>
                    <SelectItem value="treatment_completed_need_svr12">Treatment Completed - Need SVR12 labs</SelectItem>
                    <SelectItem value="treatment_incomplete">Treatment Incomplete</SelectItem>
                    <SelectItem value="mia_unable_to_connect">MIA - unable to connect to care</SelectItem>
                    <SelectItem value="incomplete_treatment_need_svr12">Incomplete treatment - Need SVR12 labs</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setCaseStatusDialogOpen(false)}>
                Cancel
              </Button>
              <Button onClick={handleUpdateCaseStatus} disabled={isUpdatingCaseStatus}>
                {isUpdatingCaseStatus ? 'Updating...' : 'Update Status'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Treatment Plan Dialog */}
        {/* Case Note Dialog */}
        <Dialog open={caseNoteDialogOpen} onOpenChange={setCaseNoteDialogOpen}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Add Case Note</DialogTitle>
              <DialogDescription>
                Add an internal note for this patient's care case.
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-4 py-4">
              <div className="space-y-2">
                <Label>Note Type</Label>
                <Select value={caseNoteType} onValueChange={setCaseNoteType}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="general">General</SelectItem>
                    <SelectItem value="outreach_call">Outreach Call</SelectItem>
                    <SelectItem value="pharmacy_issue">Pharmacy Issue</SelectItem>
                    <SelectItem value="side_effects_counseling">Side Effects Counseling</SelectItem>
                    <SelectItem value="adherence_concern">Adherence Concern</SelectItem>
                    <SelectItem value="prior_auth_update">Prior Auth Update</SelectItem>
                    <SelectItem value="appointment">Appointment</SelectItem>
                    <SelectItem value="lab_result_discussion">Lab Result Discussion</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Note</Label>
                <SmartPhraseTextarea
                  rows={6}
                  value={caseNoteBody}
                  onChange={setCaseNoteBody}
                  placeholder="Type a template code (e.g., .FOLLOWUP2WK) or enter text (minimum 10 characters)..."
                />
                <p className="text-xs text-muted-foreground">
                  Tip: Type a dot followed by uppercase letters (e.g., .FOLLOWUP2WK) to insert a template
                </p>
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setCaseNoteDialogOpen(false)}>
                Cancel
              </Button>
              <Button onClick={handleCreateCaseNote} disabled={isSavingCaseNote || caseNoteBody.length < 10}>
                {isSavingCaseNote ? 'Saving...' : 'Add Note'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Lab Result Upload Dialog */}
        <Dialog open={labUploadDialogOpen} onOpenChange={setLabUploadDialogOpen}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Upload Lab Result</DialogTitle>
              <DialogDescription>
                Upload result for {selectedLabOrder?.order_type_label}
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-4 py-4">
              <div className="space-y-2">
                <Label>Result File</Label>
                <Input
                  type="file"
                  accept=".pdf,.doc,.docx,image/*"
                  onChange={(e) => setLabResultFile(e.target.files?.[0] || null)}
                />
                <p className="text-xs text-muted-foreground">
                  Accepted formats: PDF, Word (.doc, .docx), PNG, JPG, JPEG
                </p>
              </div>
              <div className="space-y-2">
                <Label>Result Value</Label>
                <Input
                  value={labResultValue}
                  onChange={(e) => setLabResultValue(e.target.value)}
                  placeholder="e.g., Positive - 1.2M IU/mL"
                />
              </div>
              <div className="space-y-2">
                <Label>Notes</Label>
                <Textarea
                  rows={4}
                  value={labResultNotes}
                  onChange={(e) => setLabResultNotes(e.target.value)}
                  placeholder="Additional notes about the result..."
                />
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setLabUploadDialogOpen(false)}>
                Cancel
              </Button>
              <Button onClick={handleUploadLabResult} disabled={isUploadingLabResult}>
                {isUploadingLabResult ? 'Uploading...' : 'Upload Result'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Create Lab Order Dialog */}
        <Dialog open={createLabOrderDialogOpen} onOpenChange={setCreateLabOrderDialogOpen}>
          <DialogContent className="max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>Create Lab Order</DialogTitle>
              <DialogDescription>
                Order a new laboratory test for this patient
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-4 py-4">
              <div className="space-y-2">
                <Label htmlFor="test-type">Test Type *</Label>
                <Select value={newLabOrderType} onValueChange={setNewLabOrderType}>
                  <SelectTrigger id="test-type">
                    <SelectValue placeholder="Select test type" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="hcv_ab">Hepatitis C Antibody</SelectItem>
                    <SelectItem value="hcv_rna">Hepatitis C Viral Load, PCR IU/mL</SelectItem>
                    <SelectItem value="cmp">Complete Metabolic Panel</SelectItem>
                    <SelectItem value="cbc">Complete Blood Count with Differential</SelectItem>
                    <SelectItem value="hep_b_surface_ab">Hepatitis B Surface Antibody</SelectItem>
                    <SelectItem value="hep_b_core_ab">Hepatitis B Core Antibody</SelectItem>
                    <SelectItem value="hep_b_surface_ag">Hepatitis B Surface Antigen</SelectItem>
                    <SelectItem value="fibrosis">Liver Fibrotest Panel, Fibrosure</SelectItem>
                    <SelectItem value="hiv">HIV ½ Ag/Ab Screen (Standard 4th Generation)</SelectItem>
                    <SelectItem value="genotype">HCV Genotype, Lipa</SelectItem>
                    <SelectItem value="pregnancy_test">Pregnancy Test: HCG qualitative</SelectItem>
                    <SelectItem value="fib4_calculation">Fib-4 Calculation</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label htmlFor="order-date">Order Date *</Label>
                <Input
                  id="order-date"
                  type="date"
                  value={newLabOrderDate}
                  onChange={(e) => setNewLabOrderDate(e.target.value)}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="status">Status</Label>
                <Select value={newLabOrderStatus} onValueChange={setNewLabOrderStatus}>
                  <SelectTrigger id="status">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="ordered">Ordered</SelectItem>
                    <SelectItem value="in_progress">In Progress</SelectItem>
                    <SelectItem value="result_received">Result Received</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label htmlFor="result-files">Result Files (Optional)</Label>
                <Input
                  id="result-files"
                  type="file"
                  accept=".pdf,.doc,.docx,image/*"
                  multiple
                  onChange={(e) => setNewLabOrderFiles(e.target.files)}
                />
                <p className="text-xs text-muted-foreground">
                  Upload result files with order. Accepted: PDF, Word (.doc, .docx), PNG, JPG, JPEG. Multiple files allowed.
                </p>
              </div>
              <div className="space-y-2">
                <Label htmlFor="result-value">Result Value (Optional)</Label>
                <Input
                  id="result-value"
                  value={newLabOrderResultValue}
                  onChange={(e) => setNewLabOrderResultValue(e.target.value)}
                  placeholder="e.g., Positive - 1.2M IU/mL"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="result-notes">Result Notes (Optional)</Label>
                <Textarea
                  id="result-notes"
                  rows={3}
                  value={newLabOrderResultNotes}
                  onChange={(e) => setNewLabOrderResultNotes(e.target.value)}
                  placeholder="Additional notes about the result..."
                />
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setCreateLabOrderDialogOpen(false)}>
                Cancel
              </Button>
              <Button onClick={handleCreateLabOrder} disabled={isCreatingLabOrder || !newLabOrderType || !newLabOrderDate}>
                {isCreatingLabOrder ? 'Creating...' : 'Create Lab Order'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Follow-Up Update Dialog */}
        <Dialog open={followUpDialogOpen} onOpenChange={setFollowUpDialogOpen}>
          <DialogContent className="max-w-2xl">
            <DialogHeader>
              <DialogTitle>
                {followUpAction === 'reschedule' ? 'Reschedule' : 'Complete'} Follow-Up
              </DialogTitle>
              <DialogDescription>
                {selectedFollowUp?.type_label}
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-4 py-4">
              {followUpAction === 'reschedule' ? (
                <div className="space-y-2">
                  <Label>New Due Date</Label>
                  <Input
                    type="date"
                    value={followUpDueOn}
                    onChange={(e) => setFollowUpDueOn(e.target.value)}
                  />
                </div>
              ) : (
                <>
                  <div className="space-y-2">
                    <Label>Adherence Notes</Label>
                    <Textarea
                      rows={3}
                      value={followUpAdherenceNotes}
                      onChange={(e) => setFollowUpAdherenceNotes(e.target.value)}
                      placeholder="Notes about patient adherence..."
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Side Effect Notes</Label>
                    <Textarea
                      rows={3}
                      value={followUpSideEffectNotes}
                      onChange={(e) => setFollowUpSideEffectNotes(e.target.value)}
                      placeholder="Any side effects reported..."
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Provider Notes</Label>
                    <Textarea
                      rows={3}
                      value={followUpProviderNotes}
                      onChange={(e) => setFollowUpProviderNotes(e.target.value)}
                      placeholder="Provider assessment and next steps..."
                    />
                  </div>
                </>
              )}
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setFollowUpDialogOpen(false)}>
                Cancel
              </Button>
              <Button onClick={handleUpdateFollowUp} disabled={isUpdatingFollowUp}>
                {isUpdatingFollowUp ? 'Saving...' : followUpAction === 'reschedule' ? 'Reschedule' : 'Complete'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Care Stage Update Dialog */}
        <Dialog open={careStageDialogOpen} onOpenChange={setCareStageDialogOpen}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Update Care Journey Stage</DialogTitle>
              <DialogDescription>
                Change the current stage for this patient's care journey.
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-4 py-4">
              <div className="space-y-2">
                <Label>Care Stage</Label>
                <Select value={selectedCareStage} onValueChange={setSelectedCareStage}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {care_stage_options.map((stage) => (
                      <SelectItem key={stage} value={stage}>
                        {formatCareStageLabel(stage)}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setCareStageDialogOpen(false)}>
                Cancel
              </Button>
              <Button onClick={handleUpdateCareStage} disabled={isUpdatingCareStage}>
                {isUpdatingCareStage ? 'Updating...' : 'Update Stage'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Prescription Information Dialog */}
        <Dialog open={prescriptionDialogOpen} onOpenChange={setPrescriptionDialogOpen}>
          <DialogContent className="max-w-2xl">
            <DialogHeader>
              <DialogTitle>Edit Prescription Information</DialogTitle>
              <DialogDescription>
                Update patient's insurance and prescription details.
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-4 py-4">
              <div className="space-y-2">
                <Label>Health Plan Name</Label>
                <Input
                  value={prescriptionPlanName}
                  onChange={(e) => setPrescriptionPlanName(e.target.value)}
                  placeholder="e.g., Blue Cross Blue Shield"
                />
              </div>
              <div className="space-y-2">
                <Label>ID Number</Label>
                <Input
                  value={prescriptionIdNumber}
                  onChange={(e) => setPrescriptionIdNumber(e.target.value)}
                  placeholder="Member ID"
                />
              </div>
              <div className="grid grid-cols-3 gap-4">
                <div className="space-y-2">
                  <Label>BIN</Label>
                  <Input
                    value={prescriptionBin}
                    onChange={(e) => setPrescriptionBin(e.target.value)}
                    placeholder="BIN"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Group Number</Label>
                  <Input
                    value={prescriptionGroupNumber}
                    onChange={(e) => setPrescriptionGroupNumber(e.target.value)}
                    placeholder="Group #"
                  />
                </div>
                <div className="space-y-2">
                  <Label>PCN</Label>
                  <Input
                    value={prescriptionPcn}
                    onChange={(e) => setPrescriptionPcn(e.target.value)}
                    placeholder="PCN"
                  />
                </div>
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setPrescriptionDialogOpen(false)}>
                Cancel
              </Button>
              <Button onClick={handleUpdatePrescription} disabled={isUpdatingPrescription}>
                {isUpdatingPrescription ? 'Saving...' : 'Save Prescription Info'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Patient Information Dialog */}
        <Dialog open={demographicsDialogOpen} onOpenChange={setDemographicsDialogOpen}>
          <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>Edit Patient Information</DialogTitle>
              <DialogDescription>
                Update all patient information including contact details and emergency contacts.
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-6 py-4">
              {/* Basic Information Section */}
              <div className="space-y-4">
                <h3 className="text-sm font-semibold text-foreground border-b pb-2">Basic Information</h3>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="legal-name">Legal Name</Label>
                    <Input
                      id="legal-name"
                      value={legalName}
                      onChange={(e) => setLegalName(e.target.value)}
                      placeholder="Full legal name"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="alias-name">Alias Name</Label>
                    <Input
                      id="alias-name"
                      value={aliasName}
                      onChange={(e) => setAliasName(e.target.value)}
                      placeholder="Preferred name or alias"
                    />
                  </div>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="sex-assigned">Sex Assigned at Birth</Label>
                  <Select value={sexAssignedAtBirth} onValueChange={setSexAssignedAtBirth}>
                    <SelectTrigger id="sex-assigned">
                      <SelectValue placeholder="Select sex assigned at birth" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="Male">Male</SelectItem>
                      <SelectItem value="Female">Female</SelectItem>
                      <SelectItem value="Other">Other</SelectItem>
                      <SelectItem value="Prefer not to say">Prefer not to say</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>

              {/* Contact Information Section */}
              <div className="space-y-4">
                <h3 className="text-sm font-semibold text-foreground border-b pb-2">Contact Information</h3>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="phone-number">Phone Number</Label>
                    <Input
                      id="phone-number"
                      value={phoneNumber}
                      onChange={(e) => setPhoneNumber(e.target.value)}
                      placeholder="555-1234"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="email">Email</Label>
                    <Input
                      id="email"
                      type="email"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      placeholder="patient@example.com"
                    />
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="zip-code">ZIP Code</Label>
                    <Input
                      id="zip-code"
                      value={zipCode}
                      onChange={(e) => setZipCode(e.target.value)}
                      placeholder="12345"
                    />
                  </div>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="home-address">Home Address</Label>
                  <Textarea
                    id="home-address"
                    rows={2}
                    value={homeAddress}
                    onChange={(e) => setHomeAddress(e.target.value)}
                    placeholder="Street address, city, state"
                  />
                </div>
              </div>

              {/* Medical Information Section */}
              <div className="space-y-4">
                <h3 className="text-sm font-semibold text-foreground border-b pb-2">Medical Information</h3>
                <div className="space-y-2">
                  <Label htmlFor="allergies">Allergies</Label>
                  <Textarea
                    id="allergies"
                    rows={2}
                    value={allergies}
                    onChange={(e) => setAllergies(e.target.value)}
                    placeholder="List any known allergies"
                  />
                </div>
              </div>

              {/* Emergency Contacts Section */}
              <div className="space-y-4">
                <h3 className="text-sm font-semibold text-foreground border-b pb-2">Emergency Contacts</h3>

                {/* Emergency Contact 1 */}
                <div className="grid grid-cols-2 gap-4 p-3 rounded-lg bg-muted/50">
                  <div className="space-y-2">
                    <Label htmlFor="emergency-contact-1-name">Emergency Contact 1 Name</Label>
                    <Input
                      id="emergency-contact-1-name"
                      value={emergencyContact1Name}
                      onChange={(e) => setEmergencyContact1Name(e.target.value)}
                      placeholder="Contact name"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="emergency-contact-1-phone">Emergency Contact 1 Phone</Label>
                    <Input
                      id="emergency-contact-1-phone"
                      value={emergencyContact1Phone}
                      onChange={(e) => setEmergencyContact1Phone(e.target.value)}
                      placeholder="555-1234"
                    />
                  </div>
                </div>

                {/* Emergency Contact 2 */}
                <div className="grid grid-cols-2 gap-4 p-3 rounded-lg bg-muted/50">
                  <div className="space-y-2">
                    <Label htmlFor="emergency-contact-2-name">Emergency Contact 2 Name</Label>
                    <Input
                      id="emergency-contact-2-name"
                      value={emergencyContact2Name}
                      onChange={(e) => setEmergencyContact2Name(e.target.value)}
                      placeholder="Contact name"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="emergency-contact-2-phone">Emergency Contact 2 Phone</Label>
                    <Input
                      id="emergency-contact-2-phone"
                      value={emergencyContact2Phone}
                      onChange={(e) => setEmergencyContact2Phone(e.target.value)}
                      placeholder="555-1234"
                    />
                  </div>
                </div>

                {/* Emergency Contact 3 */}
                <div className="grid grid-cols-2 gap-4 p-3 rounded-lg bg-muted/50">
                  <div className="space-y-2">
                    <Label htmlFor="emergency-contact-3-name">Emergency Contact 3 Name</Label>
                    <Input
                      id="emergency-contact-3-name"
                      value={emergencyContact3Name}
                      onChange={(e) => setEmergencyContact3Name(e.target.value)}
                      placeholder="Contact name"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="emergency-contact-3-phone">Emergency Contact 3 Phone</Label>
                    <Input
                      id="emergency-contact-3-phone"
                      value={emergencyContact3Phone}
                      onChange={(e) => setEmergencyContact3Phone(e.target.value)}
                      placeholder="555-1234"
                    />
                  </div>
                </div>
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setDemographicsDialogOpen(false)}>
                Cancel
              </Button>
              <Button onClick={handleUpdateDemographics} disabled={isUpdatingDemographics}>
                {isUpdatingDemographics ? 'Saving...' : 'Save Patient Information'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Add Medication Dialog */}
        <Dialog open={medicationDialogOpen} onOpenChange={setMedicationDialogOpen}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Add Medication</DialogTitle>
              <DialogDescription>
                Add a new active medication for this patient.
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-4 py-4">
              <div className="space-y-2">
                <Label>Medication Name *</Label>
                <Input
                  value={newMedicationName}
                  onChange={(e) => setNewMedicationName(e.target.value)}
                  placeholder="e.g., Aspirin 81mg daily"
                />
              </div>
              <div className="space-y-2">
                <Label>Notes (optional)</Label>
                <Textarea
                  rows={3}
                  value={newMedicationNotes}
                  onChange={(e) => setNewMedicationNotes(e.target.value)}
                  placeholder="Additional information about this medication"
                />
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setMedicationDialogOpen(false)}>
                Cancel
              </Button>
              <Button onClick={handleAddMedication} disabled={isAddingMedication || !newMedicationName}>
                {isAddingMedication ? 'Adding...' : 'Add Medication'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Treatment Edit Dialog */}
        <Dialog open={treatmentDialogOpen} onOpenChange={setTreatmentDialogOpen}>
          <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>{treatment ? 'Edit' : 'Add'} Treatment Information</DialogTitle>
              <DialogDescription>
                Manage the patient's HCV treatment regimen, plan, and related information.
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-4 py-4">
              <div className="space-y-2">
                <Label>Treatment Regimen *</Label>
                <Select value={treatmentRegimenName} onValueChange={setTreatmentRegimenName}>
                  <SelectTrigger>
                    <SelectValue placeholder="Select regimen..." />
                  </SelectTrigger>
                  <SelectContent>
                    {treatment_options.regimen_options.map((regimen) => (
                      <SelectItem key={regimen} value={regimen}>
                        {regimen}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label>Treatment Duration *</Label>
                <Select value={treatmentDurationLabel} onValueChange={setTreatmentDurationLabel}>
                  <SelectTrigger>
                    <SelectValue placeholder="Select duration..." />
                  </SelectTrigger>
                  <SelectContent>
                    {treatment_options.duration_options.map((duration) => (
                      <SelectItem key={duration} value={duration}>
                        {duration}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label>Treatment Directions</Label>
                <Select value={treatmentDirections} onValueChange={setTreatmentDirections}>
                  <SelectTrigger>
                    <SelectValue placeholder="Select directions..." />
                  </SelectTrigger>
                  <SelectContent>
                    {treatment_options.directions_options.map((direction) => (
                      <SelectItem key={direction} value={direction}>
                        {direction}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label>Treatment Start Date *</Label>
                <Input
                  type="date"
                  value={treatmentStartDate}
                  onChange={(e) => setTreatmentStartDate(e.target.value)}
                />
              </div>

              {treatmentCompletionDate && (
                <div className="space-y-2">
                  <Label>Treatment Completion Date (Estimated)</Label>
                  <Input
                    type="date"
                    value={treatmentCompletionDate}
                    readOnly
                    className="bg-muted cursor-not-allowed"
                  />
                  <p className="text-xs text-muted-foreground">
                    Calculated based on treatment duration. This is when the patient should be due for follow-up.
                  </p>
                </div>
              )}

              <div className="space-y-2">
                <Label>Prior Authorization Status</Label>
                <Select value={treatmentPriorAuthStatus} onValueChange={setTreatmentPriorAuthStatus}>
                  <SelectTrigger>
                    <SelectValue placeholder="Select status..." />
                  </SelectTrigger>
                  <SelectContent>
                    {treatment_options.prior_auth_statuses.map((status) => (
                      <SelectItem key={status} value={status}>
                        {status.replace('_', ' ').replace(/\b\w/g, (l) => l.toUpperCase())}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {care_case && (
                <div className="space-y-2">
                  <Label>Treatment Plan Instructions</Label>
                  <Textarea
                    rows={8}
                    value={treatmentPlanInstructions}
                    onChange={(e) => setTreatmentPlanInstructions(e.target.value)}
                    placeholder="Enter detailed instructions for the patient's treatment plan..."
                  />
                  <p className="text-xs text-muted-foreground">
                    Optional: Provide detailed guidance for the patient's care team
                  </p>
                </div>
              )}
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setTreatmentDialogOpen(false)}>
                Cancel
              </Button>
              <Button onClick={handleUpdateTreatment} disabled={isUpdatingTreatment || !treatmentRegimenName || !treatmentDurationLabel || !treatmentStartDate}>
                {isUpdatingTreatment ? 'Updating...' : 'Save Treatment'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Problem List Dialog */}
        <Dialog open={problemListDialogOpen} onOpenChange={setProblemListDialogOpen}>
          <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>Add Diagnosis to Problem List</DialogTitle>
              <DialogDescription>
                Add a new diagnosis with ICD-10 code for billing purposes.
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-4 py-4">
              {/* Mode Toggle */}
              <div className="flex items-center justify-between pb-2 border-b">
                <span className="text-sm font-medium">Entry Mode</span>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={() => {
                    setUseManualEntry(!useManualEntry)
                    setSelectedIcd10CodeId(null)
                    setIcd10SearchQuery('')
                    setIcd10CodeOptions([])
                    setNewDiagnosisIcd10Code('')
                    setNewDiagnosisName('')
                  }}
                >
                  {useManualEntry ? 'Switch to Dropdown' : 'Switch to Manual Entry'}
                </Button>
              </div>

              {/* Dropdown Mode */}
              {!useManualEntry && (
                <div className="space-y-2">
                  <Label>Search ICD-10 Code *</Label>
                  <Popover>
                    <PopoverTrigger asChild>
                      <Button
                        variant="outline"
                        role="combobox"
                        className="w-full justify-between"
                      >
                        {selectedIcd10CodeId
                          ? icd10CodeOptions.find((code) => code.id === selectedIcd10CodeId)?.display_label || 'Select code...'
                          : 'Search by code or diagnosis name...'}
                        <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent className="w-[600px] p-0">
                      <div className="p-2 border-b">
                        <Input
                          placeholder="Type at least 2 characters to search..."
                          value={icd10SearchQuery}
                          onChange={(e) => {
                            setIcd10SearchQuery(e.target.value)
                            fetchIcd10Codes(e.target.value)
                          }}
                          className="w-full"
                        />
                      </div>
                      <div className="max-h-[300px] overflow-y-auto">
                        {isLoadingCodes ? (
                          <div className="p-4 text-center text-sm text-muted-foreground">
                            Loading codes...
                          </div>
                        ) : icd10CodeOptions.length === 0 ? (
                          <div className="p-4 text-center text-sm text-muted-foreground">
                            {icd10SearchQuery.length < 2
                              ? 'Type at least 2 characters to search'
                              : 'No codes found'}
                          </div>
                        ) : (
                          <div className="p-1">
                            {icd10CodeOptions.map((code) => (
                              <button
                                key={code.id}
                                onClick={() => {
                                  setSelectedIcd10CodeId(code.id)
                                  document.body.click() // Close popover
                                }}
                                className="w-full text-left px-2 py-2 text-sm hover:bg-accent rounded-sm"
                              >
                                {code.display_label}
                              </button>
                            ))}
                          </div>
                        )}
                      </div>
                    </PopoverContent>
                  </Popover>
                  {selectedIcd10CodeId && (
                    <p className="text-xs text-muted-foreground">
                      Selected: {icd10CodeOptions.find((code) => code.id === selectedIcd10CodeId)?.display_label}
                    </p>
                  )}
                </div>
              )}

              {/* Manual Entry Mode */}
              {useManualEntry && (
                <>
                  <div className="space-y-2">
                    <Label>ICD-10 Code *</Label>
                    <Input
                      value={newDiagnosisIcd10Code}
                      onChange={(e) => setNewDiagnosisIcd10Code(e.target.value)}
                      placeholder="e.g., B18.2"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Diagnosis Name *</Label>
                    <Input
                      value={newDiagnosisName}
                      onChange={(e) => setNewDiagnosisName(e.target.value)}
                      placeholder="e.g., Chronic Hepatitis C, without Hepatic Coma"
                    />
                  </div>
                </>
              )}

              {/* Common Fields */}
              <div className="space-y-2">
                <Label>Onset Date (optional)</Label>
                <Input
                  type="date"
                  value={newDiagnosisOnsetDate}
                  onChange={(e) => setNewDiagnosisOnsetDate(e.target.value)}
                />
              </div>
              <div className="space-y-2">
                <Label>Notes (optional)</Label>
                <Textarea
                  rows={3}
                  value={newDiagnosisNotes}
                  onChange={(e) => setNewDiagnosisNotes(e.target.value)}
                  placeholder="Additional clinical notes"
                />
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setProblemListDialogOpen(false)}>
                Cancel
              </Button>
              <Button
                onClick={handleAddDiagnosis}
                disabled={
                  isAddingDiagnosis ||
                  (useManualEntry
                    ? !newDiagnosisIcd10Code || !newDiagnosisName
                    : !selectedIcd10CodeId)
                }
              >
                {isAddingDiagnosis ? 'Adding...' : 'Add Diagnosis'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Questionnaire Responses Modal */}
        {risk_assessment && risk_assessment.questionnaire_by_section && (
          <QuestionnaireResponsesModal
            open={questionnaireModalOpen}
            onOpenChange={setQuestionnaireModalOpen}
            riskLevel={risk_assessment.risk_level_label}
            riskColor={risk_assessment.risk_color}
            completedAt={risk_assessment.completed_at}
            counselorSummary={risk_assessment.counselor_summary}
            questionnaireBySection={risk_assessment.questionnaire_by_section}
          />
        )}

        {/* Risk Assessment Intake Dialog */}
        <RiskAssessmentIntakeDialog
          open={assessmentIntakeOpen}
          onOpenChange={setAssessmentIntakeOpen}
          patientId={patient.id}
          patientName={patient.display_name}
          questions={questionnaire_questions}
        />

        {/* File Preview Modal */}
        <Dialog open={filePreviewOpen} onOpenChange={setFilePreviewOpen}>
          <DialogContent className="max-w-6xl h-[90vh]">
            <DialogHeader>
              <DialogTitle>{previewFile?.filename || 'File Preview'}</DialogTitle>
              <DialogDescription>
                {previewFile?.content_type === 'application/pdf'
                  ? 'PDF Document'
                  : previewFile?.content_type?.startsWith('image/')
                    ? 'Image File'
                    : previewFile?.content_type?.includes('word') || previewFile?.content_type?.includes('document')
                      ? 'Word Document'
                      : 'File'}
              </DialogDescription>
            </DialogHeader>
            <div className="flex-1 overflow-auto">
              {previewFile && (
                <>
                  {previewFile.content_type === 'application/pdf' ? (
                    <iframe
                      src={previewFile.url}
                      className="w-full h-[calc(90vh-120px)] border-0"
                      title={previewFile.filename}
                    />
                  ) : previewFile.content_type?.startsWith('image/') ? (
                    <div className="flex items-center justify-center h-[calc(90vh-120px)]">
                      <img
                        src={previewFile.url}
                        alt={previewFile.filename}
                        className="max-w-full max-h-full object-contain"
                      />
                    </div>
                  ) : (
                    <div className="flex flex-col items-center justify-center h-[calc(90vh-120px)] space-y-4">
                      <FileTextIcon className="h-20 w-20 text-muted-foreground" />
                      <div className="text-center">
                        <p className="text-lg font-medium">{previewFile.filename}</p>
                        <p className="text-sm text-muted-foreground mt-2">
                          Preview not available for this file type
                        </p>
                        <p className="text-xs text-muted-foreground mt-1">
                          Click "Download" to open the file
                        </p>
                      </div>
                    </div>
                  )}
                </>
              )}
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setFilePreviewOpen(false)}>
                Close
              </Button>
              <Button
                onClick={() => {
                  if (previewFile) {
                    window.open(previewFile.url, '_blank')
                  }
                }}
              >
                Download
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Delete Patient Confirmation Dialog */}
        <AlertDialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>Delete Patient</AlertDialogTitle>
              <AlertDialogDescription>
                This action will remove this patient from the active patient list.
                All patient data will be preserved for audit purposes.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <div className="py-4">
              <Label htmlFor="confirm-delete">Type DELETE to confirm:</Label>
              <Input
                id="confirm-delete"
                value={deleteConfirmText}
                onChange={(e) => setDeleteConfirmText(e.target.value)}
                placeholder="DELETE"
                className="mt-2"
              />
            </div>
            <AlertDialogFooter>
              <AlertDialogCancel onClick={() => setDeleteConfirmText('')}>Cancel</AlertDialogCancel>
              <AlertDialogAction
                onClick={handleDelete}
                disabled={deleteConfirmText !== 'DELETE'}
                className="bg-red-600 hover:bg-red-700"
              >
                Delete Patient
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </div>
    </div>
  )
}
